<template>
    <div>
        <div class="container" style="margin-bottom: 151px;">
            <div v-if="loading">
            </div>
            <div v-else>
                <div v-if="task">
                    <h4 class="text-center mt-3">{{ $t('taskDetail.taskNumber', { id: task.id }) }}</h4>
                    <div v-if="projectClosed" class="alert alert-warning py-2 my-3 small">
                        <i class="fa-solid fa-lock me-2"></i>{{ $t('taskDetail.projectClosed') }}
                    </div>
                    <ProgressSteps :currentStage="task.stage_id" :totalStages="3" :selectedStage="selectedStage"
                        :finished="task.finished" @stage-selected="selectedStage = $event" />
                    <TaskComponent :task="task" linkable :editable="canWriteTask && !task.confirmed && can('Priv_AppTasksEditDate')" @update-estimated-date="handleUpdateEstimatedDate" />

                    <div class="accordion shadow mt-3" id="accordionNotes" v-if="[0, 1].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': !task.notes }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseNotes" aria-expanded="false" aria-controls="collapseNotes">
                                    {{ $t('taskDetail.description') }}
                                </button>
                            </h2>
                            <div id="collapseNotes" class="accordion-collapse collapse show"
                                data-bs-parent="#accordionNotes">
                                <div class="accordion-body">
                                    <div v-if="task.notes" class="small text-multiline text-break">
                                        {{ task.notes }}
                                    </div>
                                    <div v-else class="fst-italic small">
                                        {{ $t('taskDetail.noDescription') }}
                                    </div>
                                    <ul v-if="task.files.length > 0" class="list-unstyled">
                                        <li class="my-2 list-group-item d-flex justify-content-between align-items-start" v-for="file in task.files" :key="file.id">
                                            <div class="flex-grow-1" style="min-width: 0;">
                                                <div v-if="file.content_type == 1" class="btn btn-light w-100 text-start text-truncate small" @click="requestFile(file)"><i class="fa-solid fa-paperclip me-2"></i>{{ file.filename }}</div>
                                                <a v-else target="_blank" :href="file.url" role="button" class="btn btn-light w-100 text-start text-truncate small"><i class="fa-solid fa-at me-2"></i>{{ file.url }}</a>
                                                <div v-if="file.notes" class="text-body-secondary text-multiline text-break mt-1"><small>{{ file.notes }}</small></div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="accordion shadow mt-3" id="accordionComments" v-if="[0, 1, 2].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button fs-6 fw-bolder" type="button" data-bs-toggle="collapse"
                                    data-bs-target="#collapseComments"
                                    :aria-expanded="isCommentExpanded ? 'true' : 'false'"
                                    aria-controls="collapseComments"
                                    :class="{ 'collapsed': !isCommentExpanded, 'text-body-tertiary': task.comments.length == 0 }">
                                    {{ $t('taskDetail.commentsInternal') }}
                                </button>
                            </h2>
                            <div id="collapseComments" class="accordion-collapse" data-bs-parent="#accordionComments"
                                :class="{ 'collapse': !isCommentExpanded, 'show': isCommentExpanded }">
                                <div class="accordion-body">
                                    <div v-if="task.comments.length > 0">
                                        <div class="card mb-3 bg-body-tertiary" v-for="(comment) in task.comments"
                                            :key="comment.id">
                                            <ul class="list-group list-group-flush">
                                                <li class="list-group-item bg-body-tertiary p-3" :id="'comment-' + comment.id"
                                                    :class="{ 'bg-warning-subtle': isCommentTarget(comment.id) }">
                                                    <h6>{{ comment.user_name }}</h6>
                                                    <div class="text-multiline" v-html="comment.comment"></div>
                                                    <div v-if="expandedNotified[comment.id]" class="small text-body-secondary mt-1">
                                                        {{ $t('taskDetail.notified') }}: {{ comment.notified_users }}</div>
                                                    <div class="row mt-2">
                                                        <div class="col small text-body-secondary">
                                                            <span v-if="comment.notified_user_ids?.length" role="button" class="me-2 text-nowrap"
                                                                :title="comment.notified_users" @click="toggleNotified(comment.id)"><i
                                                                    class="fa-regular fa-bell me-1"></i>{{ comment.notified_user_ids.length }}</span>
                                                            {{ formatTimestamp(comment.created_on) }}
                                                        </div>
                                                        <div class="col-auto small text-end">
                                                            <span v-if="comment.user_id == currentUserId" role="button" @click="openEditCommentModal(comment)" class="me-3"><i
                                                                    class="fa-regular fa-pen-to-square me-2"></i>{{ $t('common.edit') }}</span>
                                                            <span role="button" @click="openCommentModal(comment)"><i
                                                                    class="fa-regular fa-comment me-2"></i>{{ $t('taskDetail.reply') }}</span>
                                                        </div>
                                                    </div>
                                                </li>
                                                <li class="list-group-item bg-light-subtle p-3 ps-5"
                                                    v-for="(reply) in comment.replies" :key="reply.id"
                                                    :id="'comment-' + reply.id"
                                                    :class="{ 'bg-warning-subtle': isCommentTarget(comment.id) }">
                                                    <h6>{{ reply.user_name }}</h6>
                                                    <div class="text-multiline" v-html="reply.comment"></div>
                                                    <div v-if="expandedNotified[reply.id]" class="small text-body-secondary mt-1">
                                                        {{ $t('taskDetail.notified') }}: {{ reply.notified_users }}</div>
                                                    <div class="row mt-2">
                                                        <div class="col small text-body-secondary">
                                                            <span v-if="reply.notified_user_ids?.length" role="button" class="me-2 text-nowrap"
                                                                :title="reply.notified_users" @click="toggleNotified(reply.id)"><i
                                                                    class="fa-regular fa-bell me-1"></i>{{ reply.notified_user_ids.length }}</span>
                                                            {{ formatTimestamp(reply.created_on) }}
                                                        </div>
                                                        <div class="col-auto small text-end">
                                                            <span v-if="reply.user_id == currentUserId" role="button" @click="openEditCommentModal(reply)"><i
                                                                    class="fa-regular fa-pen-to-square me-2"></i>{{ $t('common.edit') }}</span>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-else class="small fst-italic">
                                        {{ $t('taskDetail.noComments') }}
                                    </div>
                                    <!-- Commenting is open to anyone who can see the task, not just
                                         the assignees, so this gates on the closed project alone. -->
                                    <div v-if="!projectClosed" class="mt-3">
                                        <button class="btn btn-primary btn-sm" @click="openModal('comment')">
                                            {{ $t('taskDetail.addComment') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Keyed by task: the view is reused across task ids, so a new task
                         re-renders the card closed. Signals are fetched only while open. -->
                    <div class="accordion shadow mt-3" id="accordionSims" v-if="simsAccounts.length > 0"
                        :key="'sims-' + task.id"
                        v-on="{ 'shown.bs.collapse': this.onSimsShown, 'hidden.bs.collapse': this.onSimsHidden }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseSims" aria-expanded="false"
                                    aria-controls="collapseSims">
                                    {{ $t('taskDetail.simsSignals') }}
                                </button>
                            </h2>
                            <div id="collapseSims" class="accordion-collapse collapse"
                                data-bs-parent="#accordionSims">
                                <div class="accordion-body">
                                    <div v-for="simsAccount in simsAccounts" :key="simsAccount.sims_account"
                                        class="card mb-3 bg-light-subtle">
                                        <div class="card-body">
                                            <div class="row">
                                                <div class="col">
                                                    <div class="form-check">
                                                        <input :id="'simsAccount-' + simsAccount.sims_account" type="checkbox"
                                                            class="form-check-input"
                                                            v-model="selectedSimsAccounts"
                                                            :value="simsAccount.sims_account"
                                                            :disabled="!canWriteTask || simsAccount.disabled">
                                                        <label class="form-check-label"
                                                            :for="'simsAccount-' + simsAccount.sims_account"><span
                                                                class="extra-small" :class="{ 'text-danger': simsAccount.disabled }">{{ simsAccount.sims_account
                                                                }}<span v-if="simsAccount.comment"> — {{ simsAccount.comment }}</span></span></label>
                                                    </div>
                                                </div>
                                                <div class="col d-flex align-items-start">
                                                    <span
                                                        :class="{
                                                            'bg-body-tertiary text-body-secondary': simsAccount.out_of_service === false, 
                                                            'bg-info-subtle text-info-emphasis': simsAccount.out_of_service === true,
                                                            'bg-danger-subtle text-danger-emphasis': simsAccount.out_of_service === null,
                                                        }"
                                                        class="badge rounded-pill ms-auto">
                                                            {{ simsAccount.out_of_service === null ? $t('taskDetail.noSignal') : (simsAccount.out_of_service ? simsAccount.out_of_service_until : $t('taskDetail.notInService')) }}
                                                        </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="form-check mb-3">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            id="selectAllCheckbox"
                                            :checked="allSelected"
                                            @change="toggleSelectAll"
                                            :disabled="!canWriteTask"
                                        />
                                        <label class="form-check-label" for="selectAllCheckbox">{{ $t('taskDetail.selectAll') }}</label>
                                    </div>
                                    <VueDatePicker v-model="outOfServiceUntil" time-picker input-class-name=""
                                        :min-date="new Date()"
                                        :max-date="getEndOfDay()"
                                        :locale="locale" :cancel-text="$t('common.cancel')" :select-text="$t('common.select')" :clearable="false" :disabled="task.finished || task.active_stage_id !== 1 || !canWriteTask"
                                        required />

                                    <div class="my-3 d-flex justify-content-between">
                                            <button class="btn btn-primary btn-sm" @click="setOutOfService()" :disabled="actionInProgress || selectedSimsAccounts.length == 0 || task.finished || task.active_stage_id !== 1 || !canWriteTask">
                                                {{ $t('taskDetail.setOutOfService') }}
                                            </button>
                                            <button class="btn btn-primary btn-sm" @click="deleteOutOfService()" :disabled="actionInProgress || selectedSimsAccounts.length == 0 || !canWriteTask">
                                                {{ $t('taskDetail.removeOutOfService') }}
                                            </button>
                                    </div>
                                
                                    <div class="row g-3 mt-0">
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <select id="simsAccountsSelect" v-model="selectedSimsAccount" class="form-select bg-body-tertiary" @change="fetchSignals">
                                                <option value="">{{ $t('taskDetail.allOption') }}</option>
                                                <option
                                                    v-for="account in simsAccounts"
                                                    :key="account.sims_account"
                                                    :value="account.sims_account"
                                                >
                                                    {{ account.sims_account }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <VueDatePicker
                                                auto-apply
                                                format="dd.MM.yyyy"
                                                v-model="dateRange"
                                                :locale="locale"
                                                :cancel-text="$t('common.cancel')"
                                                :select-text="$t('common.select')"
                                                :range="{ }"
                                                :clearable="true"
                                                :closeOnScroll="true"
                                                @update:modelValue="onDateRangeChange"
                                            />
                                        </div>
                                        <div class="col-12 col-sm-6 col-md-3 d-flex align-items-center">
                                            <div class="form-check form-switch ms-4">
                                                <input class="form-check-input" type="checkbox"
                                                    id="autoUpdateSwitch"
                                                    v-model="autoUpdate"
                                                    :disabled="!simsAccounts.length" />
                                                <label class="form-check-label" for="autoUpdateSwitch">
                                                {{ $t('taskDetail.autoUpdate') }}
                                                </label>
                                            </div>
                                        </div>
                                        <div class="col-12 col-sm-6 col-md-3 d-flex align-items-center justify-content-sm-end">
                                            <a :href="signalsPageHref" target="_blank" rel="noopener"
                                                @click.prevent="openSignalsPage"
                                                class="btn btn-outline-secondary btn-sm text-nowrap" :title="$t('common.openInNewTab')">
                                                <i class="fa-solid fa-arrow-up-right-from-square me-1"></i>{{ $t('common.openInNewTab') }}
                                            </a>
                                        </div>
                                    </div>
                                    <div class="table-responsive">
                                        <table class="table mt-3 small">
                                            <thead>
                                                <tr>
                                                    <th scope="col">{{ $t('taskDetail.sims.time') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.account') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.signal') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.type') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.description') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.disposition') }}</th>
                                                    <th scope="col">{{ $t('taskDetail.sims.comment') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-if="apiSignalsError">
                                                    <td colspan="7" class="text-center">{{ $t('taskDetail.noConnection') }}</td>
                                                </tr>
                                                <tr v-else-if="!apiSignalsError && signals.length === 0">
                                                    <td colspan="7" class="text-center">{{ $t('taskDetail.noSignals') }}</td>
                                                </tr>
                                                <tr v-else v-for="signal in signals" :key="signal.EventSummaryId">
                                                    <td>{{ signal.received }}</td>
                                                    <td>{{ signal.account }}</td>
                                                    <td>{{ signal.zone }}</td>
                                                    <td>{{ signal.eventType }}</td>
                                                    <td>{{ signal.description }}</td>
                                                    <td>{{ signal.disposition }}</td>
                                                    <td>{{ signal.comment }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionMemo4" v-if="[0, 1].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': !task.project_memo4 && !task.project_hashlink }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseMemo4" aria-expanded="true" aria-controls="collapseMemo4">
                                    {{ $t('taskDetail.technicalInfo') }}
                                </button>
                            </h2>
                            <div id="collapseMemo4" class="accordion-collapse collapse show"
                                data-bs-parent="#accordionMemo4">
                                <div class="accordion-body">
                                    <div v-if="task.project_hashlink" class="small text-multiline text-break mb-3">
                                        <a :href="task.project_hashlink" target="_blank">
                                        {{ $t('taskDetail.buildingCard') }}
                                        </a>
                                    </div>
                                    <div v-if="task.project_memo4" class="small text-multiline text-break"
                                        :class="{ 'memo4-clamped': !memo4Expanded }" ref="memo4Text">
                                        <span v-html="linkifyText(task.project_memo4)"></span>
                                    </div>
                                    <div v-else class="fst-italic small">
                                        {{ $t('taskDetail.noDescription') }}
                                    </div>
                                    <!-- Below the v-else, never between it and the memo above: an
                                         element in between would re-pair the else with this block. -->
                                    <div v-if="task.project_memo4 && memo4Overflows" class="text-center">
                                        <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none"
                                            @click="memo4Expanded = !memo4Expanded">
                                            <small>{{ memo4Expanded ? $t('common.showLess') : $t('common.showMore') }}</small>
                                            <i class="fa-solid ms-2" :class="memo4Expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                        </button>
                                    </div>
                                    <ul v-if="task.project_files.length > 0" class="list-unstyled">
                                        <li class="my-2 list-group-item d-flex justify-content-between align-items-start" v-for="file in task.project_files" :key="file.id">
                                            <div class="flex-grow-1" style="min-width: 0;">
                                                <div class="btn btn-light w-100 text-start text-truncate" @click="requestProjectFile(file)"><i class="fa-solid fa-paperclip me-2"></i><small>{{ file.filename }}</small></div>
                                                <div v-if="file.notes" class="text-body-secondary text-multiline text-break mt-1"><small>{{ file.notes }}</small></div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionReminders"
                        v-if="task.project_id && [0, 1, 2].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': reminders.length == 0 }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseReminder" aria-expanded="false"
                                    aria-controls="collapseReminder">
                                    {{ $t('taskDetail.reminders') }}
                                </button>
                            </h2>
                            <div id="collapseReminder" class="accordion-collapse collapse"
                                data-bs-parent="#accordionReminders">
                                <div class="accordion-body">
                                    <ul v-if="reminders.length > 0" class="list-unstyled">
                                        <li v-for="(reminder, index) in reminders" :key="reminder.id" class="my-2 d-flex align-items-start">
                                            <input class="form-check-input me-1 flex-shrink-0" type="checkbox" :value="reminder.id"
                                                :disabled="reminder.is_permanent || !canWriteTask"
                                                v-model="reminder.checked"
                                                @change="handleCheckboxChange(reminder, index)">
                                            <!-- Not a <label>: the checkbox deletes the reminder, so the
                                                 text must not forward clicks to it. -->
                                            <div class="flex-grow-1">
                                                <div>{{ reminder.reminder }}</div>
                                                <div class="text-secondary"><small>{{ $t('taskDetail.added') }}: {{ reminder.created_by }} {{ formatTimestamp(reminder.created_on) }}</small></div>
                                            </div>
                                            <span v-if="canWriteTask && !reminder.is_permanent" role="button"
                                                class="ms-2 small text-nowrap" @click="openEditReminderModal(reminder)"><i
                                                    class="fa-regular fa-pen-to-square me-2"></i>{{ $t('common.edit') }}</span>
                                        </li>
                                    </ul>
                                    <div v-else class="fst-italic small">{{ $t('taskDetail.noReminders') }}</div>
                                    <div class="mt-3">
                                        <button v-if="canWriteTask" class="btn btn-primary btn-sm" @click="openModal('reminder')">
                                            {{ $t('taskDetail.addNew') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionNeedsAttention"
                        v-if="task.object_id && [0, 1].includes(selectedStage) && task.object_needs_attention.length > 0"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': task.object_needs_attention.length == 0 }"
                                    data-bs-toggle="collapse" data-bs-target="#collapseNeedsAttention"
                                    aria-expanded="false" aria-controls="collapseNeedsAttention">
                                    {{ $t('taskDetail.previousIssues') }}
                                </button>
                            </h2>
                            <div id="collapseNeedsAttention" class="accordion-collapse collapse"
                                data-bs-parent="#accordionNeedsAttention">
                                <div class="accordion-body">
                                    <ul v-if="task.object_needs_attention.length > 0"
                                        class="list-group list-group-flush">
                                        <li v-for="item in task.object_needs_attention" :key="item.id"
                                            class="my-2 list-group-item">
                                            <router-link :to="`/tasks/${item.id}`"
                                                class="text-decoration-none d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div class="small fw-bolder">{{ item.name }}</div>
                                                    <div class="small my-1">{{ item.notes }}</div>
                                                    <div class="text-secondary"><small>{{ item.finished_by_name }} {{ item.finished_on ? formatTimestamp(item.finished_on) : '' }}</small></div>
                                                </div>
                                                <div><i class="ms-2 fa-solid fa-triangle-exclamation" :class="{ 'text-danger': item.needs_attention == 1, 'text-warning': item.needs_attention == 2, 'text-primary': item.needs_attention == 4 }"></i>
                                                </div>
                                            </router-link>
                                        </li>
                                    </ul>
                                    <div v-else class="fst-italic small">{{ $t('taskDetail.noIssues') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionContact"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': !hasAnyContacts }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseContact" aria-expanded="false"
                                    aria-controls="collapseContact">
                                    {{ $t('taskDetail.customerContacts') }}
                                </button>
                            </h2>
                            <div id="collapseContact" class="accordion-collapse collapse"
                                data-bs-parent="#accordionContact">
                                <div class="accordion-body">
                                    <div v-if="taskContacts.length > 0">
                                        <h6>{{ $t('taskDetail.taskContacts') }}</h6>
                                        <div v-for="contact in taskContacts" :key="contact.id">
                                            <ul class="list-unstyled">
                                                <li>
                                                    <i class="fa-regular fa-user text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.name }}</small></span>
                                                </li>
                                                <li>
                                                    <a :href="`tel:${contact.phone}`">
                                                        <i class="fa-solid fa-phone text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                    </a>
                                                </li>
                                                <li>
                                                    <a :href="`mailto:${contact.email}`">
                                                        <i class="fa-regular fa-envelope text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-if="customerContacts.length > 0">
                                        <h6>{{ $t('taskDetail.customerContactPerson') }}</h6>
                                        <div v-for="contact in customerContacts" :key="contact.id">
                                            <ul class="list-unstyled">
                                                <li>
                                                    <i class="fa-regular fa-user text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.name }}</small></span>
                                                </li>
                                                <li>
                                                    <a :href="`tel:${contact.phone}`">
                                                        <i class="fa-solid fa-phone text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                    </a>
                                                </li>
                                                <li>
                                                    <a :href="`mailto:${contact.email}`">
                                                        <i class="fa-regular fa-envelope text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-if="projectCustomerContacts.length > 0">
                                        <h6>{{ $t('taskDetail.projectCardContacts') }}</h6>
                                        <div v-for="contact in projectCustomerContacts" :key="contact.id">
                                            <ul class="list-unstyled">
                                                <li>
                                                    <i class="fa-regular fa-user text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.name }}</small></span>
                                                </li>
                                                <li>
                                                    <a :href="`tel:${contact.phone}`">
                                                        <i class="fa-solid fa-phone text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                    </a>
                                                </li>
                                                <li>
                                                    <a :href="`mailto:${contact.email}`">
                                                        <i class="fa-regular fa-envelope text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-if="maintenanceProjectContacts.length > 0">
                                        <h6>{{ $t('taskDetail.maintenanceProjectCardContacts') }}</h6>
                                        <div v-for="contact in maintenanceProjectContacts" :key="contact.id">
                                            <ul class="list-unstyled">
                                                <li>
                                                    <i class="fa-regular fa-user text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.name }}</small></span>
                                                </li>
                                                <li>
                                                    <a :href="`tel:${contact.phone}`">
                                                        <i class="fa-solid fa-phone text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                    </a>
                                                </li>
                                                <li>
                                                    <a :href="`mailto:${contact.email}`">
                                                        <i class="fa-regular fa-envelope text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-if="!hasAnyContacts" class="fst-italic small">{{ $t('taskDetail.noContacts')
                                        }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionProjectContact"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': companyContacts.length == 0 }"
                                    data-bs-toggle="collapse" data-bs-target="#collapseProjectContact"
                                    aria-expanded="false" aria-controls="collapseProjectContact">
                                    {{ $t('taskDetail.companyContacts') }}
                                </button>
                            </h2>
                            <div id="collapseProjectContact" class="accordion-collapse collapse"
                                data-bs-parent="#accordionProjectContact">
                                <div class="accordion-body">
                                    <div v-if="companyContacts.length > 0">
                                        <div v-for="contact in companyContacts" :key="contact.id">
                                            <h6>{{ contact.title }}</h6>
                                            <ul class="list-unstyled">
                                                <li>
                                                    <i class="fa-regular fa-user text-danger"></i>
                                                    <span class="ps-2"><small>{{ contact.name }}</small></span>
                                                </li>
                                                <li>
                                                    <a :href="`tel:${contact.phone}`">
                                                        <i class="fa-solid fa-phone text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.phone }}</small></span>
                                                    </a>
                                                </li>
                                                <li>
                                                    <a :href="`mailto:${contact.email}`">
                                                        <i class="fa-regular fa-envelope text-danger"></i>
                                                        <span class="ps-2"><small>{{ contact.email }}</small></span>
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div v-else class="fst-italic small">{{ $t('taskDetail.noContacts') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionFiles" v-if="[0, 1, 2].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': task.technician_files.length == 0 && uploads.length === 0 }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseFiles" aria-expanded="false" aria-controls="collapseFiles">
                                    {{ $t('taskDetail.attachments') }}
                                </button>
                            </h2>
                            <div id="collapseFiles" class="accordion-collapse collapse"
                                data-bs-parent="#accordionFiles">
                                <div class="accordion-body">
                                    <ul v-if="task.technician_files.length > 0" class="list-unstyled">
                                        <li class="my-2 list-group-item d-flex justify-content-between align-items-start" v-for="(file, fileIndex) in task.technician_files" :key="file.id">
                                            <template v-if="editingFileId === file.id">
                                                <div class="input-group input-group-sm w-100">
                                                    <input type="text" class="form-control small" v-model="editingFilename"
                                                        @keyup.enter="saveFilename(file)" @keyup.esc="cancelEditFilename" ref="filenameInput">
                                                    <span class="input-group-text small text-body-tertiary">{{ fileExtension(file.filename) }}</span>
                                                </div>
                                                <button class="ms-2 btn-icon bg-success-subtle text-success-emphasis" @click="saveFilename(file)" :disabled="actionInProgress" :title="$t('common.save')"><i class="fa-solid fa-check"></i></button>
                                                <button class="ms-2 btn-icon bg-secondary-subtle text-secondary-emphasis" @click="cancelEditFilename" :title="$t('common.cancel')"><i class="fa-solid fa-xmark"></i></button>
                                            </template>
                                            <template v-else>
                                                <div class="flex-grow-1" style="min-width: 0;">
                                                    <div v-if="file.content_type == 1" class="btn btn-light w-100 text-start text-truncate small" @click="requestFile(file)"><i class="fa-solid fa-paperclip me-2"></i>{{ file.filename }}</div>
                                                    <a v-else target="_blank" :href="file.url" role="button" class="btn btn-light w-100 text-start text-truncate small"><i class="fa-solid fa-at me-2"></i>{{ file.url }}</a>
                                                    <div v-if="file.notes" class="text-body-secondary text-multiline text-break mt-1"><small>{{ file.notes }}</small></div>
                                                </div>
                                                <i v-if="file.sent_via_email" class="fa-solid fa-envelope-circle-check ms-2 align-self-center text-success" :title="$t('taskDetail.sentViaEmail')"></i>
                                                <button v-if="file.content_type == 1 && !task.confirmed && canWriteTask" class="ms-2 btn-icon bg-primary-subtle text-primary-emphasis" @click="startEditFilename(file)" :title="$t('common.edit')"><i class="fa-regular fa-pen-to-square"></i></button>
                                                <button v-if="!task.confirmed && canWriteTask" class="ms-2 btn-icon bg-danger-subtle text-danger-emphasis" @click="deleteTaskFile(file, fileIndex)" :disabled="actionInProgress"><i class="fa-regular fa-trash-can"></i></button>
                                            </template>
                                        </li>
                                    </ul>
                                    <ul v-if="uploads.length > 0" class="list-unstyled mb-0">
                                        <li class="my-2 list-group-item" v-for="entry in uploads" :key="'up-' + entry.uid">
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div class="w-100 text-start text-truncate small text-body-tertiary">
                                                    <i class="fa-solid fa-arrow-up-from-bracket me-2"></i>{{ entry.name }}
                                                </div>
                                                <button class="ms-2 btn-icon bg-secondary-subtle text-secondary-emphasis"
                                                    @click="cancelUpload(entry)" :title="$t('taskDetail.cancelUpload')">
                                                    <i class="fa-solid fa-xmark"></i>
                                                </button>
                                            </div>
                                            <div class="progress mt-2" style="height: 4px;">
                                                <div class="progress-bar"
                                                    :class="{ 'progress-bar-striped progress-bar-animated': entry.status !== 'uploading' }"
                                                    :style="{ width: (entry.status === 'queued' ? 100 : entry.progress) + '%' }"></div>
                                            </div>
                                            <div class="small text-body-tertiary mt-1">
                                                {{ uploadStatusLabel(entry) }}<span v-if="entry.status === 'uploading'"> {{ entry.progress }}%</span>
                                            </div>
                                        </li>
                                    </ul>
                                    <div v-if="task.technician_files.length === 0 && uploads.length === 0" class="fst-italic small">{{ $t('taskDetail.noAttachments') }}</div>
                                    <input type="file" @change="uploadFiles" ref="fileUpload" style="display: none;" multiple>
                                    <button v-if="!task.confirmed && canWriteTask" class="btn btn-sm btn-primary mt-3"
                                        @click="openFileDialog">
                                        <i class="fa-solid fa-arrow-up-from-bracket me-2"></i><small>{{ $t('taskDetail.addFile') }}</small>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionLocation" v-if="[0].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': task.files.project_address }"
                                    data-bs-toggle="collapse" data-bs-target="#collapseLocation" aria-expanded="false"
                                    aria-controls="collapseLocation">
                                    {{ $t('taskDetail.location') }}
                                </button>
                            </h2>
                            <div id="collapseLocation" class="accordion-collapse collapse"
                                data-bs-parent="#accordionLocation">
                                <div class="accordion-body text-multiline">
                                    <div v-if="task.project_address">
                                        <div class="small my-3">{{ $t('taskDetail.address') }}: {{ task.project_address }}</div>
                                        <div class="row">
                                            <div class="col-auto">
                                                <a :href="googleMapsUrl" :target="mapTarget" rel="noopener"
                                                    class="btn btn-primary btn-sm w-100">
                                                    Google Maps
                                                </a>
                                            </div>
                                            <div class="col-auto">
                                                <a :href="wazeUrl" :target="mapTarget" rel="noopener"
                                                    class="btn btn-primary btn-sm w-100">
                                                    Waze
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="task.project_coordinates">
                                        <div class="small my-3">{{ $t('taskDetail.coordinates') }}: {{ task.project_coordinates }}</div>
                                        <div class="row">
                                            <div class="col-auto">
                                                <a :href="googleMapsUrlCoordinates" :target="mapTarget" rel="noopener"
                                                    class="btn btn-primary btn-sm w-100">
                                                    Google Maps
                                                </a>
                                            </div>
                                            <div class="col-auto">
                                                <a :href="wazeUrlCoordinates" :target="mapTarget" rel="noopener"
                                                    class="btn btn-primary btn-sm w-100">
                                                    Waze
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!task.project_address && !task.project_coordinates" class="small fst-italic">
                                        {{ $t('taskDetail.noLocation') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <input type="file" @change="uploadSubtaskFile" ref="subtaskFileUpload" style="display: none;"
                        accept="image/*" capture="environment">

                    <div class="accordion shadow mt-3" id="accordionAct" v-if="[1].includes(selectedStage) && task.maintenance_acts.length > 0 && canWriteTask && !task.finished && !task.confirmed"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseAct" aria-expanded="false"
                                    aria-controls="collapseAct">
                                    {{ $t('taskDetail.actForm') }}
                                </button>
                            </h2>
                            <div id="collapseAct" class="accordion-collapse collapse"
                                data-bs-parent="#accordionAct">
                                <div class="accordion-body p-0">
                                    <ul class="list-group list-group-flush">
                                        <li v-for="act in task.maintenance_acts" :key="`${act.id}-${act.act_id ?? 'new'}`"
                                            class="list-group-item d-flex justify-content-between align-items-center">
                                            <router-link v-if="act.editable !== false" :to="actPath(act)"
                                                class="text-decoration-none flex-grow-1"
                                                :class="{ 'text-body-secondary': !act.act_id }">
                                                <i v-if="!act.act_id" class="fa-solid fa-plus fa-fw me-1"></i>{{ act.name }}
                                            </router-link>
                                            <span v-else class="flex-grow-1 text-body-secondary">{{ act.name }}</span>
                                            <router-link v-if="act.editable !== false" :to="actPath(act)" target="_blank"
                                                class="text-decoration-none ms-3" :title="$t('common.openInNewTab')">
                                                <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                            </router-link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionChecklists"
                        v-if="[1].includes(selectedStage) && checklistSubtasks.length > 0"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseChecklistsContent"
                                    aria-expanded="false" aria-controls="collapseChecklistsContent">
                                    {{ $t('taskDetail.checklist') }}
                                    <span v-if="checklistHasMandatory" class="text-danger ms-1"
                                        :title="$t('taskDetail.completionMandatory')"
                                        :aria-label="$t('taskDetail.completionMandatory')">*</span>
                                </button>
                            </h2>
                            <div id="collapseChecklistsContent" class="accordion-collapse collapse"
                                data-bs-parent="#accordionChecklists">
                                <div class="accordion-body">
                                    <div v-for="subtask in checklistSubtasks" :key="subtask.id">
                                        <div v-if="subtask.checklist_istitle">
                                            <div class="fw-bold mb-3">{{ subtask.name }}</div>
                                        </div>
                                        <div v-else>
                                            <div class="accordion mb-3" :id="'checklistAccordion' + subtask.id">
                                                <div class="accordion-item"
                                                    :class="{ 'bg-success-subtle': subtask.finished, 'bg-danger-subtle': !subtask.finished, 'border-danger border-3': blockingSubtaskIds.includes(subtask.id) }">
                                                    <h3 class="accordion-header">
                                                        <button class="accordion-button collapsed" type="button"
                                                            :class="{ 'bg-success-subtle': subtask.finished, 'bg-danger-subtle': !subtask.finished }"
                                                            :data-bs-toggle="'collapse'"
                                                            :data-bs-target="'#collapseNestedChecklist' + subtask.id"
                                                            aria-expanded="false"
                                                            :aria-controls="'collapseNestedChecklist' + subtask.id">
                                                            {{ subtask.name }}
                                                            <span v-if="subtask.notes_mandatory"
                                                                class="text-danger ms-1"
                                                                :title="$t('taskDetail.completionMandatory')"
                                                                :aria-label="$t('taskDetail.completionMandatory')">*</span>
                                                        </button>
                                                    </h3>
                                                    <div :id="'collapseNestedChecklist' + subtask.id"
                                                        class="accordion-collapse collapse"
                                                        :data-bs-parent="'#checklistAccordion' + subtask.id">
                                                        <div class="accordion-body">
                                                            <div class="mb-3">
                                                                <label :for="'subTaskNotes' + subtask.id"
                                                                    class="form-label"><small>{{ $t('taskDetail.comment') }}</small></label>
                                                                <textarea :id="'subTaskNotes' + subtask.id"
                                                                    class="form-control bg-body"
                                                                    :placeholder="$t('taskDetail.comment')"
                                                                    :disabled="subtask.finished || !canEditChecklist"
                                                                    v-model="subtask.notes"></textarea>
                                                            </div>

                                                            <button
                                                                v-if="!subtask.finished && canEditChecklist"
                                                                class="btn btn-primary"
                                                                @click="pickFromGallery(subtask)">
                                                                <i class="fa-solid fa-arrow-up-from-bracket me-2"></i><small>{{ $t('taskDetail.addImage') }}</small>
                                                            </button>
                                                            <button
                                                                v-if="!subtask.finished && canEditChecklist"
                                                                class="btn btn-primary"
                                                                @click="takePhoto(subtask)">
                                                                <i class="fa-solid fa-camera me-2"></i><small>{{ $t('taskDetail.camera') }}</small>
                                                            </button>
                                                            <ul class="list-unstyled mt-3">
                                                                <li class="my-2 list-group-item d-flex justify-content-between align-items-start"
                                                                    v-for="(file, fileIndex) in subtask.files"
                                                                    :key="file.id">
                                                                    <div class="btn btn-light w-100 text-start text-truncate"
                                                                        @click="requestSubtaskFile(subtask, file)">
                                                                        <i
                                                                            class="fa-solid fa-paperclip me-2"></i><small>{{ file.filename }}</small>
                                                                    </div>
                                                                    <button
                                                                        v-if="!subtask.finished && canEditChecklist"
                                                                        class="ms-2 btn-icon bg-danger-subtle text-danger-emphasis"
                                                                        @click="deleteSubtaskFile(subtask, file, fileIndex)"
                                                                        :disabled="actionInProgress">
                                                                        <i class="fa-regular fa-trash-can"></i>
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                            <div class="row mb-3">
                                                                <div class="col">
                                                                    <div class="form-check">
                                                                        <input
                                                                            :id="'subTaskNeedsAttention' + subtask.id"
                                                                            type="checkbox" class="form-check-input"
                                                                            v-model="subtask.needs_attention"
                                                                            :disabled="subtask.finished || !canEditChecklist">
                                                                        <label class="form-check-label"
                                                                            :for="'subTaskNeedsAttention' + subtask.id">{{ $t('taskDetail.defect') }}</label>
                                                                    </div>

                                                                </div>
                                                                <div class="col text-end">
                                                                    <div v-if="subtask.needs_attention_id > 0">
                                                                        <span
                                                                            class="badge rounded-pill bg-body-tertiary py-2 px-3">
                                                                            <i class="fa-solid fa-triangle-exclamation"
                                                                                :class="{ 'text-danger': subtask.needs_attention_id === 1, 'text-warning': subtask.needs_attention_id === 2, 'text-success': subtask.needs_attention_id === 3, 'text-primary': subtask.needs_attention_id === 4 }">
                                                                            </i>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div class="mb-3">
                                                                <div class="form-check">
                                                                    <input :id="'subTaskFinished' + subtask.id"
                                                                        type="checkbox" class="form-check-input"
                                                                        @change="updateSubtask(subtask)"
                                                                        v-model="subtask.finished"
                                                                        :disabled="!canEditChecklist || savingSubtaskIds.includes(subtask.id)">
                                                                    <label class="form-check-label"
                                                                        :for="'subTaskFinished' + subtask.id">{{ $t('enums.stage.finished') }}</label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionMaterials" v-if="[1, 2].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': this.materials.length == 0 }"
                                    data-bs-toggle="collapse" data-bs-target="#collapseMaterials" aria-expanded="false"
                                    aria-controls="collapseMaterials">
                                    {{ $t('taskDetail.materials') }}
                                </button>
                            </h2>
                            <div id="collapseMaterials" class="accordion-collapse collapse"
                                data-bs-parent="#accordionMaterials">
                                <div class="accordion-body">
                                    <ul v-if="this.materials.length > 0" class="list-group list-group-flush">
                                        <li v-for="resource in this.materials" :key="resource.id"
                                            class="list-group-item d-flex justify-content-between align-items-start px-0">
                                            <div v-if="!resource.istitle" class="me-auto">
                                                <div class="fw-bold">{{ resource.resource_name }}</div>
                                                <div class="text-body-secondary text-multiline"><small>{{ resource.sn }}</small></div>
                                                <div class="text-body-secondary text-multiline"><small>{{ resource.description }}</small></div>
                                                <div v-if="resource.notes" class="text-body-secondary text-multiline"><small><i class="fa-solid fa-lock fa-fw me-1"></i>{{ resource.notes }}</small></div>
                                                <div v-if="!resource.planned" class="text-body-secondary"><small>{{ formatTimestamp(resource.issued_on, false) }} - {{ resource.username }}</small></div>
                                            </div>
                                            <div v-else class="me-auto">
                                                <div class="fw-bold">{{ resource.description }}</div>
                                            </div>
                                            <div v-if="!resource.istitle" class="fw-bold text-nowrap mx-2">
                                                <span v-if="!resource.planned"> {{ resource.amount ? resource.amount : 0 }}</span>
                                                <span v-if="resource.planned">{{ $t('task.planned') }}: {{ resource.planned }}</span>
                                            </div>
                                            <button v-if="!resource.istitle && currentUserId == resource.user_id && canEditWork" class="btn-icon"
                                                @click="openEditModal('materials', resource)"><i
                                                    class="fa-solid fa-pencil fa-fw"></i></button>
                                            <button v-if="resource.planned && canEditWork" class="btn-icon"
                                                @click="openAddModalWithPreselect('materials', resource)"><i
                                                    class="fa-solid fa-plus fa-fw"></i></button>
                                        </li>
                                    </ul>
                                    <div v-else class="small fst-italic">
                                        {{ $t('taskDetail.noMaterials') }}
                                    </div>
                                    <div v-if="canEditWork" class="mt-3">
                                        <button class="btn btn-primary btn-sm" @click="openModal('materials')">
                                            {{ $t('taskDetail.addMaterial') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionHours" v-if="[1, 2].includes(selectedStage) && task.hours_visible"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': this.hours.length == 0 }" data-bs-toggle="collapse"
                                    data-bs-target="#collapseHours" aria-expanded="false" aria-controls="collapseHours">
                                    {{ $t('taskDetail.timeSection') }}
                                </button>
                            </h2>
                            <div id="collapseHours" class="accordion-collapse collapse"
                                data-bs-parent="#accordionHours">
                                <div class="accordion-body">
                                    <div v-if="task.max_quote_points !== null" class="mb-3"><span class="small fw-medium">{{ $t('taskDetail.budgetedPoints') }}:</span> <span class="small">{{ task.max_quote_points }}</span></div>
                                    <ul v-if="this.hours.length > 0" class="list-group list-group-flush">
                                        <li v-for="resource in this.hours" :key="resource.id"
                                            class="list-group-item d-flex justify-content-between align-items-start px-0">
                                            <div v-if="!resource.istitle" class="me-auto">
                                                <div class="fw-bold">{{ resource.resource_name }}</div>
                                                <div class="text-body-secondary text-multiline"><small>{{ resource.sn }}</small></div>
                                                <div class="text-body-secondary text-multiline"><small>{{ resource.description }}</small></div>
                                                <div v-if="resource.notes" class="text-body-secondary text-multiline"><small><i class="fa-solid fa-lock fa-fw me-1"></i>{{ resource.notes }}</small></div>
                                                <div v-if="!resource.planned" class="text-body-secondary"><small>{{ formatTimestamp(resource.issued_on, false) }} - {{ resource.username }}</small></div>
                                            </div>
                                            <div v-else class="me-auto">
                                                <div class="fw-bold">{{ resource.description }}</div>
                                            </div>
                                            <div v-if="!resource.istitle" class="fw-bold text-nowrap mx-2">
                                                <span v-if="!resource.planned"> {{ resource.amount ? resource.amount : 0 }}</span>
                                                <span v-if="resource.planned">{{ $t('task.planned') }}: {{ resource.planned }}</span>
                                            </div>
                                            <button v-if="!resource.istitle && currentUserId == resource.user_id && canEditWork" class="btn-icon"
                                                @click="openEditModal('hours', resource)"><i
                                                    class="fa-solid fa-pencil fa-fw"></i></button>
                                            <button v-if="resource.planned && canEditWork" class="btn-icon"
                                                @click="openAddModalWithPreselect('hours', resource)"><i
                                                    class="fa-solid fa-plus fa-fw"></i></button>
                                        </li>
                                    </ul>
                                    <div v-else class="small fst-italic">
                                        {{ $t('taskDetail.noEntries') }}
                                    </div>
                                    <div v-if="canEditWork" class="mt-3">
                                        <button class="btn btn-primary btn-sm" @click="openModal('hours')">
                                            {{ $t('taskDetail.addTime') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionInfo" v-if="[0, 1].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="false"
                                    aria-controls="collapseInfo">
                                    {{ $t('taskDetail.technicianInfo') }}
                                </button>
                            </h2>
                            <div id="collapseInfo" class="accordion-collapse collapse" data-bs-parent="#accordionInfo">
                                <div class="accordion-body">
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item p-0">
                                            <h6>{{ $t('taskDetail.responsiblePersons') }}</h6>
                                            <div><small>{{ $t('taskDetail.issuedBy') }}: {{ task.issued_by_name }}</small></div>
                                            <div><small>{{ $t('taskDetail.responsible') }}: {{ task.directed_to_name }}</small></div>
                                            <div class="d-flex align-items-center gap-2">
                                                <small>{{ $t('taskDetail.workers') }}: {{ task.users.map(user => user.name).join(', ') }}</small>
                                                <button v-if="canManageTaskUsers" type="button"
                                                    class="btn-icon btn-icon-small bg-primary-subtle text-primary-emphasis"
                                                    :title="$t('taskDetail.editWorkers')" :aria-label="$t('taskDetail.editWorkers')"
                                                    @click="openModal('taskUsers')"><i class="fa-regular fa-pen-to-square"></i></button>
                                            </div>
                                            <div v-if="userStatus"><small>{{ $t('taskDetail.transport') }}: {{ formattedTransportTime }}</small></div>
                                            <div v-if="userStatus"><small>{{ $t('taskDetail.work') }}: {{ formattedWorkTime }}</small></div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionFinalize" v-if="[1, 2].includes(selectedStage)"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button fs-6 fw-bolder" type="button"
                                    :class="{ 'text-body-tertiary': !task.notes2 && task.signed_files.length == 0 }"
                                    data-bs-toggle="collapse" data-bs-target="#collapseFinalize" aria-expanded="false"
                                    aria-controls="collapseFinalize">
                                    {{ $t('taskDetail.workDone') }}
                                </button>
                            </h2>
                            <div id="collapseFinalize" class="accordion-collapse"
                                data-bs-parent="#accordionFinalize">
                                <div class="accordion-body">
                                    <div class="mb-3">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label for="notes2Input" class="form-label mb-0"><small>{{ $t('taskDetail.workDescriptionLabel') }}</small></label>
                                            <button v-if="textTemplates.length > 0 && canEditWork"
                                                type="button" class="btn btn-sm btn-outline-primary"
                                                @click="openModal('textTemplate')"><small>{{ $t('taskDetail.insertTemplate') }}</small></button>
                                        </div>
                                        <div class="autogrow" :data-value="task.notes2 || ''">
                                            <textarea id="notes2Input" class="form-control" v-model="task.notes2"
                                                :placeholder="$t('taskDetail.descriptionPlaceholder')"
                                                :disabled="!canEditWork"
                                                @input="scheduleNotes2Save" @blur="flushNotes2Save"
                                                :class="{ 'is-invalid': canEditWork && task.active_stage_id == 2 && (!task.notes2 || task.notes2.trim() === '') }"></textarea>
                                        </div>
                                    </div>
                                    <ul v-if="task.signed_files.length > 0" class="list-unstyled">
                                        <li class="my-2 list-group-item d-flex justify-content-between align-items-start"
                                            v-for="(file, fileIndex) in task.signed_files" :key="file.id">
                                            <template v-if="editingFileId === file.id">
                                                <div class="input-group input-group-sm w-100">
                                                    <input type="text" class="form-control small" v-model="editingFilename"
                                                        @keyup.enter="saveFilename(file)" @keyup.esc="cancelEditFilename" ref="filenameInput">
                                                    <span class="input-group-text small text-body-tertiary">{{ fileExtension(file.filename) }}</span>
                                                </div>
                                                <button class="ms-2 btn-icon bg-success-subtle text-success-emphasis" @click="saveFilename(file)" :disabled="actionInProgress" :title="$t('common.save')"><i class="fa-solid fa-check"></i></button>
                                                <button class="ms-2 btn-icon bg-secondary-subtle text-secondary-emphasis" @click="cancelEditFilename" :title="$t('common.cancel')"><i class="fa-solid fa-xmark"></i></button>
                                            </template>
                                            <template v-else>
                                                <div class="btn btn-light w-100 text-start text-truncate"
                                                    @click="requestFile(file)">
                                                    <i class="fa-solid fa-paperclip me-2"></i><i v-if="file.sent_via_email" class="fa-solid fa-envelope-circle-check me-2 align-self-center text-success" :title="$t('taskDetail.sentViaEmail')"></i><small>{{ file.filename }}</small></div>
                                                
                                                <button v-if="!task.confirmed && canWriteTask" class="ms-2 btn-icon bg-primary-subtle text-primary-emphasis" @click="startEditFilename(file)" :title="$t('common.edit')"><i class="fa-regular fa-pen-to-square"></i></button>
                                                <button v-if="!task.confirmed && canWriteTask" class="ms-2 btn-icon bg-danger-subtle text-danger-emphasis" @click="deleteSignedFile(file, fileIndex)" :disabled="actionInProgress"><i class="fa-regular fa-trash-can"></i></button>
                                            </template>
                                        </li>
                                    </ul>
                                    <div class="mt-3">
                                        <button v-if="canEditWork" class="btn btn-primary btn-sm me-3" @click="saveNotes2" :disabled="notes2Saving">
                                            {{ $t('common.save') }}
                                        </button>
                                        <button v-if="!task.finished && task.active_stage_id == 2 && canWriteTask" class="btn btn-primary btn-sm me-3" @click="checkAndOpenSignatureModal">
                                            {{ $t('taskDetail.generatePdf') }}
                                        </button>
                                        <small v-if="notes2Status === 'saving'" class="text-body-tertiary">{{ $t('common.saving') }}</small>
                                        <small v-else-if="notes2Status === 'saved'" class="text-success">{{ $t('common.saved') }}</small>
                                        <small v-else-if="notes2Status === 'error'" class="text-danger">{{ $t('common.saveFailed') }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionEmail" v-if="[2].includes(selectedStage) && task.signed_files.length > 0 && canWriteTask && !task.confirmed && can('Priv_AppTasksFollowup')"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseEmail" aria-expanded="false"
                                    aria-controls="collapseEmail">
                                    {{ $t('taskDetail.sendToCustomer') }}
                                </button>
                            </h2>
                            <div id="collapseEmail" class="accordion-collapse collapse"
                                data-bs-parent="#accordionEmail">
                                <div class="accordion-body">
                                    <div class="mb-3">
                                        <label for="emailInput" class="form-label"><small>{{ $t('taskDetail.email') }}</small></label>
                                        <div class="form-control d-flex flex-wrap gap-1 align-items-center h-auto">
                                            <span v-for="addr in emails" :key="addr" class="badge text-bg-secondary d-inline-flex align-items-center gap-1">
                                                {{ addr }}
                                                <button type="button" class="btn-close btn-close-white" style="font-size: .6em"
                                                    @click="removeEmail(addr)" :aria-label="$t('delete')"></button>
                                            </span>
                                            <input type="email" v-model="emailInput" id="emailInput" list="emailList"
                                                class="border-0 flex-grow-1 p-0" style="outline: none; min-width: 12ch"
                                                @keydown.enter.prevent="commitEmail" @keydown.tab="commitEmail"
                                                @keydown.,.prevent="commitEmail" @keydown.delete="backspaceEmail"
                                                @change="commitEmail" @blur="commitEmail">
                                        </div>
                                        <datalist id="emailList">
                                            <option v-for="contact in availableEmailContacts" :key="contact.email" :value="contact.email">{{ contact.name }} &lt;{{ contact.email }}&gt;</option>
                                        </datalist>
                                    </div>
                                    <div class="mb-3">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label for="emailBodyInput" class="form-label mb-0"><small>{{ $t('taskDetail.emailBody') }}</small></label>
                                            <button v-if="emailTemplates.length > 0"
                                                type="button" class="btn btn-sm btn-outline-primary"
                                                @click="openModal('emailTemplate')"><small>{{ $t('taskDetail.insertEmailTemplate') }}</small></button>
                                        </div>
                                        <div class="autogrow" :data-value="emailBody || ''">
                                            <textarea id="emailBodyInput" ref="emailBodyInput" class="form-control" v-model="emailBody" :placeholder="$t('taskDetail.contentPlaceholder')"></textarea>
                                        </div>
                                    </div>
                                    <div class="mb-3" v-if="task.signed_files.length > 0">
                                        <label class="form-label"><small>{{ $t('taskDetail.signedDocuments') }}</small></label>
                                        <ul class="list-unstyled mb-0">
                                            <li class="form-check my-1" v-for="file in task.signed_files" :key="file.id">
                                                <input class="form-check-input" type="checkbox" :value="file.id" v-model="selectedSignedFileIds" :id="'signedFileCheck' + file.id">
                                                <label class="form-check-label small text-truncate" :for="'signedFileCheck' + file.id">
                                                    <i class="fa-solid fa-paperclip me-2"></i>{{ file.filename }}
                                                </label>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="mb-3" v-if="task.technician_files.length > 0">
                                        <label class="form-label"><small>{{ $t('taskDetail.attachmentsAsLinks') }}</small></label>
                                        <ul class="list-unstyled mb-0">
                                            <li class="form-check my-1" v-for="file in task.technician_files" :key="file.id">
                                                <input class="form-check-input" type="checkbox" :value="file.id" v-model="selectedTechnicianFileIds" :id="'technicianFileCheck' + file.id">
                                                <label class="form-check-label small text-truncate" :for="'technicianFileCheck' + file.id">
                                                    <i :class="file.content_type == 1 ? 'fa-solid fa-paperclip me-2' : 'fa-solid fa-at me-2'"></i>{{ file.content_type == 1 ? file.filename : file.url }}
                                                </label>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="mt-3">
                                        <button class="btn btn-primary btn-sm" @click="sendMail" :disabled="actionInProgress">
                                            {{ $t('taskDetail.sendEmail') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionSentEmails" v-if="[2].includes(selectedStage) && task.sent_emails && task.sent_emails.length > 0"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseSentEmails" aria-expanded="false"
                                    aria-controls="collapseSentEmails">
                                    {{ $t('taskDetail.sentEmails') }}
                                </button>
                            </h2>
                            <div id="collapseSentEmails" class="accordion-collapse collapse"
                                data-bs-parent="#accordionSentEmails">
                                <div class="accordion-body">
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="sentEmail in task.sent_emails" :key="sentEmail.id" class="my-1">
                                            <router-link :to="{ name: 'TaskEmail', params: { taskId: task.id, emailId: sentEmail.id } }"
                                                class="btn btn-light w-100 text-start small d-flex justify-content-between align-items-center">
                                                <span class="text-truncate me-2"><i class="fa-solid fa-envelope me-2"></i>{{ sentEmail.to_address }}</span>
                                                <small class="text-body-secondary flex-shrink-0">{{ formatTimestamp(sentEmail.sent_on) }}</small>
                                            </router-link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="accordion shadow mt-3" id="accordionNewTask" v-if="[2].includes(selectedStage) && canWriteTask && can('Priv_AppTasksFollowup')"
                        v-on="{ 'shown.bs.collapse': this.scrollToElement }">
                        <div class="accordion-item border-0">
                            <h2 class="accordion-header">
                                <button class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                    data-bs-toggle="collapse" data-bs-target="#collapseNewTask" aria-expanded="false"
                                    aria-controls="collapseNewTask">
                                    {{ $t('taskDetail.followup') }}
                                </button>
                            </h2>
                            <div id="collapseNewTask" class="accordion-collapse collapse"
                                data-bs-parent="#accordionNewTask">
                                <div class="accordion-body">
                                    <div class="form-check form-switch pb-3 mb-3 border-bottom">
                                        <input class="form-check-input" type="checkbox"
                                            id="needsFollowupSwitch"
                                            :checked="!!task.needs_followup"
                                            @change="toggleNeedsFollowup($event)"
                                            :disabled="actionInProgress || task.finished">
                                        <label class="form-check-label" for="needsFollowupSwitch">
                                            {{ $t('taskDetail.needsFollowupBtn') }}
                                        </label>
                                    </div>
                                    <div class="mb-3">
                                        <label for="newTaskTitleInput" class="form-label"><small>{{ $t('taskDetail.titleLabel') }}</small></label>
                                        <input type="text" v-model="newTaskTitle" id="newTaskTitleInput" class="form-control">
                                    </div>
                                    <div class="mt-3">
                                        <button class="btn btn-primary btn-sm" @click="addNewTask" :disabled="actionInProgress">
                                            {{ $t('taskDetail.createNewTask') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="fixed-bottom action-bar p-3 mt-3 shadow">
                        <div v-if="!isUserInvolved" class="row">
                            <div class="col">
                                <button class="btn btn-outline-primary w-100" @click="goBack()">
                                    {{ $t('common.back') }}
                                </button>
                            </div>
                            <div v-if="!task.confirmed && !isUserInvolved && !projectClosed && can('Priv_AppTasksEditUsers')" class="col">
                                <button class="btn btn-primary w-100" @click="addMe" :disabled="actionInProgress">{{ $t('taskDetail.addMe') }}</button>
                            </div>
                        </div>
                        <div v-else-if="!task.confirmed" class="row gy-2">
                            <div v-if="task && task.actions && task.actions.length <= 1" class="col">
                                <button class="btn btn-outline-primary w-100" @click="goBack()">
                                    {{ $t('common.back') }}
                                </button>
                            </div>
                            <div :class="actionColumnClass" v-for="action in task.actions" :key="action.name">
                                <button class="btn w-100"
                                    @click="performAction(action.name)"
                                    :disabled="actionInProgress"
                                    :class="{ 'btn-outline-primary': action.pause, 'btn-primary': !action.pause }">
                                    {{ action.title }}
                                </button>
                            </div>
                        </div>
                        <div v-else class="row">
                            <div class="col">
                                <button class="btn btn-outline-primary w-100" @click="goBack()">
                                    {{ $t('common.back') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <NotFound v-else :item="$t('taskDetail.taskItem')" />
            </div>
        </div>
    </div>
    <div v-if="showModal" class="item-modal bg-body-tertiary">
        <div class="modal-content">
            <div v-if="modalType === 'hours' || modalType === 'materials'">
                <add-item-modal :type="modalType" :taskId="task.id" @close="showModal = false" :itemToEdit="currentItem" :preselectResource="preselectResource" :profileId="task.profile_id" :priorityId="task.priority_id" :accountingstatus_id="task.accountingstatus_id"
                    @item-added="addItem" @item-deleted="handleItemDeleted" @item-updated="updateItem">
                </add-item-modal>
            </div>
            <div v-else-if="modalType === 'reminder'">
                <add-reminder-modal :projectId="task.project_id" :reminderToEdit="reminderToEdit" @close="showModal = false"
                    @reminder-added="addReminder" @reminder-updated="updateReminder">
                </add-reminder-modal>
            </div>
            <div v-else-if="modalType === 'comment'">
                <add-comment-modal :taskId="task.id" @close="showModal = false" @comment-added="addComment"
                    :originalComment="currentItem" :commentToEdit="commentToEdit">
                </add-comment-modal>
            </div>
            <div v-else-if="modalType === 'signature'">
                <add-signature-modal :taskId="task.id" @close="showModal = false" @signature-added="addSignature"
                    :notes2="task.notes2">
                </add-signature-modal>
            </div>
            <div v-else-if="modalType === 'textTemplate'">
                <pick-text-template-modal :templates="textTemplates" @close="showModal = false"
                    @template-picked="insertTemplate">
                </pick-text-template-modal>
            </div>
            <div v-else-if="modalType === 'emailTemplate'">
                <pick-text-template-modal :templates="emailTemplates" :title="$t('modal.pickEmailTemplate')"
                    @close="showModal = false" @template-picked="insertEmailTemplate">
                </pick-text-template-modal>
            </div>
            <div v-else-if="modalType === 'taskUsers'">
                <manage-task-users-modal :taskId="task.id" :assignedUsers="task.users"
                    :currentUserId="currentUserId" :canManageOthers="canManageOtherTaskUsers"
                    :selfLocked="selfTimerRunning" :confirm="confirmAction"
                    @close="showModal = false" @users-updated="applyTaskUsers" @self-removed="handleSelfRemoved">
                </manage-task-users-modal>
            </div>
        </div>
    </div>
    <!-- Simple Confirm Modal -->
    <div class="modal fade" id="confirmModal" tabindex="-1" ref="confirmModal">
    <div class="modal-dialog">
        <div class="modal-content">
        <div class="modal-body">
            <p>{{ confirmMessage }}</p>
            <div v-if="confirmShowFollowup" class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="confirmNeedsFollowupSwitch" v-model="confirmFollowupValue">
                <label class="form-check-label" for="confirmNeedsFollowupSwitch">{{ $t('taskDetail.needsFollowupBtn') }}</label>
            </div>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">{{ $t('common.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="confirmYes">{{ confirmLabel }}</button>
        </div>
        </div>
    </div>
    </div>

</template>

<script>
import axios from '@/services/axios';
import { backDeltaSkippingPath } from '@/router';
import { setDocumentTitle } from '@/composables/usePageTitle';
import { useFormatTimestamp } from '@/composables/useFormatTimestamp';
import { downloadBlob } from '@/utils/downloadFile';
import {
    googleMapsAddressUrl, googleMapsCoordinatesUrl,
    wazeAddressUrl, wazeCoordinatesUrl, mapLinkTarget,
} from '@/utils/mapLinks';
import TaskComponent from '@/components/TaskComponent.vue';
import ProgressSteps from '@/components/ProgressSteps.vue';
import AddItemModal from '@/components/AddItemModal.vue';
import AddReminderModal from '@/components/AddReminderModal.vue';
import AddCommentModal from '@/components/AddCommentModal.vue';
import AddSignatureModal from '@/components/AddSignatureModal.vue';
import PickTextTemplateModal from '@/components/PickTextTemplateModal.vue';
import ManageTaskUsersModal from '@/components/ManageTaskUsersModal.vue';
import NotFound from '@/components/NotFound.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { Modal } from 'bootstrap';
import { can as canPerm } from '@/composables/usePerms';
import { loadTemplates, getTextTemplates, getEmailTemplates } from '@/composables/useTextTemplates';
import { useI18n } from 'vue-i18n';

// Which subtask a pending photo belongs to, kept across a WebView reload.
// sessionStorage, not localStorage: per-tab, and gone when the app is closed.
const PENDING_SUBTASK_UPLOAD = 'pendingSubtaskUpload';

// Two at a time: enough that progress is visibly moving on more than one row,
// few enough that a weak mobile connection is not split across many streams.
const MAX_CONCURRENT_UPLOADS = 2;

export default {
    data() {
        return {
            task: null,
            reminders: [],
            simsAccounts: [],
            signals: [],
            selectedSimsAccounts: [],
            selectedStage: null,
            showModal: false,
            modalType: null,
            currentItem: null,
            commentToEdit: null,
            // Comment id -> whether its "Notified: ..." line is shown. Tap to
            // toggle rather than a hover tooltip, which touch screens lack.
            expandedNotified: {},
            reminderToEdit: null,
            preselectResource: null,
            currentSubtaskId: null,
            savingSubtaskIds: [],
            // Files picked but not yet stored server-side. Rendered as placeholder
            // rows in the attachment list so a slow upload is visible instead of
            // silent. Kept out of `task`, which fetchTaskDetail() replaces wholesale.
            uploads: [],        // { uid, file, name, progress, status, controller }
            activeUploads: 0,   // in-flight workers, capped at MAX_CONCURRENT_UPLOADS
            uploadUid: 0,       // monotonic id, used as the :key for placeholder rows
            // Mandatory checklist items left unfinished, highlighted red until
            // they are ticked. Filled by the finish pre-check and by the
            // server's 409 responses.
            blockingSubtaskIds: [],
            // Technical info is long enough to bury the rest of the task, so it
            // renders clamped until expanded. The toggle only appears once the
            // text is measured as actually overflowing.
            memo4Expanded: false,
            memo4Overflows: false,
            emailBody: null,
            emails: [],
            emailInput: '',
            selectedSignedFileIds: [],
            selectedTechnicianFileIds: [],
            editingFileId: null,
            editingFilename: '',
            loading: true,
            newTaskTitle: null,
            selectedSimsAccount: '',
            // On by default, but polling only runs while the SIMS card is open
            // (simsOpen) -- see syncSignalsPolling().
            autoUpdate: true,
            autoUpdateIntervalId: null,
            simsOpen: false,
            dateRange: null,
            apiSignalsError: false,
            userStatus: null,

            workSeconds: 0,
            transportSeconds: 0,

            timerIntervalId: null,
            outOfServiceUntil: {
                hours: this.setDefaultTime().getHours(),
                minutes: this.setDefaultTime().getMinutes()
            },
            confirmResolve: null,
            confirmMessage: null,
            confirmLabel: null,
            // Optional needs_followup switch, only shown in the finish confirm.
            confirmShowFollowup: false,
            confirmFollowupValue: false,
            // Resolves once the confirm modal has finished hiding, so a second
            // question can be asked right after the first one was answered.
            confirmHidden: Promise.resolve(),
            actionInProgress: false,
            // notes2 autosaves itself — technicians kept losing work by navigating
            // away without pressing Save. Own flags, not actionInProgress, which is
            // shared by ~10 handlers and would silently drop an autosave.
            notes2SaveTimer: null,
            notes2StatusTimer: null,
            notes2Saving: false,
            notes2SavedValue: '',
            notes2Status: null, // null | 'saving' | 'saved' | 'error'
        };
    },
    components: {
        TaskComponent,
        ProgressSteps,
        AddItemModal,
        AddReminderModal,
        AddCommentModal,
        NotFound,
        AddSignatureModal,
        PickTextTemplateModal,
        ManageTaskUsersModal,
        VueDatePicker
    },
    setup() {
        const { formatTimestamp } = useFormatTimestamp();
        const { locale } = useI18n();
        return { formatTimestamp, locale };
    },
    mounted() {
        this.restorePendingSubtask();
        this.fetchTaskDetail();
        // Fallback seed for sessions that predate the feature or survived a reload;
        // no-ops once the cache is filled, so opening tasks in a row costs nothing.
        loadTemplates();
        window.addEventListener('beforeunload', this.warnUnsavedNotes2);
        this.watchMemo4Overflow();
    },
    beforeUnmount() {
        // clear interval on teardown
        this.stopTimer();
        this.stopAutoUpdate();
        this.memo4Observer?.disconnect();
        window.removeEventListener('beforeunload', this.warnUnsavedNotes2);
        clearTimeout(this.notes2SaveTimer);
        clearTimeout(this.notes2StatusTimer);
        // Last-chance save. Not awaited — the component is already going away.
        if (this.canEditWork && this.isNotes2Dirty()) {
            axios.patch(`tasks/${this.task.id}`, { notes2: this.task.notes2 })
                .catch(error => console.error('Error saving notes2 on unmount', error));
        }
    },
    // Flush before the router tears the view down, so a fast navigation right
    // after typing still persists.
    async beforeRouteLeave(to, from, next) {
        await this.flushNotes2Save();
        next();
    },
    watch: {
        // Re-attach on the first load and on every task the view is reused for;
        // a different task starts clamped again.
        'task.project_memo4'() {
            this.memo4Expanded = false;
            this.watchMemo4Overflow();
        },
        // The memo lives in a stage-gated v-if, so it is created (or re-created)
        // when the user switches into stage 0/1 without the memo itself changing.
        selectedStage() {
            this.watchMemo4Overflow();
        },
        '$route'(to, from) {
            // Check if the route parameter you're interested in has changed
            if (to.params.taskId !== from.params.taskId) {
                // The component is reused across task ids, so a photo picked for
                // the previous task must not attach to a subtask of this one.
                this.clearPendingSubtask();
                // The SIMS card is re-keyed per task and so comes back closed; its
                // hidden event never fires for the old one, so stop polling here.
                this.stopAutoUpdate();
                this.simsOpen = false;
                this.signals = [];
                // Call the method to fetch new task data
                this.fetchTaskDetail();
            }
        },
        autoUpdate(newValue) {
            if (newValue) {
                this.syncSignalsPolling();
            } else {
                // Turning it off keeps the rows already shown; no refetch.
                this.stopAutoUpdate();
            }
        },
        simsOpen() {
            this.syncSignalsPolling();
        },
        // No accounts unmounts the card (v-if) without a hidden event.
        'simsAccounts.length'(count) {
            if (!count) {
                this.simsOpen = false;
            }
        },
    },
    methods: {
        can(perm) {
            return canPerm(perm);
        },
        async confirmAction(message = this.$t('taskDetail.confirmDefault'), { confirmLabel, showFollowup = false, followupValue = false } = {}) {
            // The others-active flow reopens this dialog right after the finish
            // confirm closed, and Bootstrap ignores show() while the previous hide
            // transition is still running — wait for it to settle first.
            await this.confirmHidden;
            return new Promise((resolve) => {
                this.confirmMessage = message;
                this.confirmLabel = confirmLabel || this.$t('common.yes');
                this.confirmShowFollowup = showFollowup;
                this.confirmFollowupValue = followupValue;
                this.confirmResolve = resolve;
                const modalEl = this.$refs.confirmModal;
                this.confirmHidden = new Promise((hidden) => {
                    // Cancel, ESC and backdrop clicks all just dismiss the modal.
                    // Resolve false on any dismissal, otherwise the awaiting caller
                    // hangs forever and never clears actionInProgress.
                    modalEl.addEventListener('hidden.bs.modal', () => {
                        this.resolveConfirm(false);
                        hidden();
                    }, { once: true });
                });
                new Modal(modalEl).show();
            });
        },
        // Settles the pending confirm promise once; later calls are no-ops.
        resolveConfirm(answer) {
            if (this.confirmResolve) {
                this.confirmResolve(answer);
                this.confirmResolve = null;
            }
        },
        confirmYes() {
            this.resolveConfirm(true);
            const modal = Modal.getInstance(this.$refs.confirmModal);
            modal.hide();
        },
        formatDate(date, date_only=false) {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            if (date_only) {
                return `${year}-${month}-${day}`;
            } else {
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }
        },
        // The memo element only exists once the task has loaded, so the observer
        // is attached then rather than at mount. Kept off `data` deliberately:
        // an observer has no business being made reactive.
        watchMemo4Overflow() {
            this.$nextTick(() => {
                this.memo4Observer?.disconnect();
                const el = this.$refs.memo4Text;
                if (!el) return;
                this.memo4Observer = new ResizeObserver(() => this.measureMemo4Overflow());
                this.memo4Observer.observe(el);
                this.measureMemo4Overflow();
            });
        },
        measureMemo4Overflow() {
            // Only meaningful while clamped: once expanded, scrollHeight equals
            // clientHeight and this would clear the flag, taking the "show less"
            // button away with it.
            if (this.memo4Expanded) return;
            const el = this.$refs.memo4Text;
            if (!el) return;
            // Bootstrap's .collapse:not(.show) is display:none, so a closed
            // accordion measures zero. Keep the last known answer instead.
            if (!el.clientHeight) return;
            this.memo4Overflows = el.scrollHeight > el.clientHeight + 1;
        },
        linkifyText(text) {
            if (!text) return '';

            const escapedText = String(text).replace(/[&<>'"]/g, (char) => {
                const entities = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;'
                };
                return entities[char];
            });

            return escapedText.replace(/(https?:\/\/[^\s<]+)/gi, (match) => {
                const trailingPunctuationMatch = match.match(/[),.;!?]+$/);
                const trailingPunctuation = trailingPunctuationMatch ? trailingPunctuationMatch[0] : '';
                const cleanUrl = trailingPunctuation ? match.slice(0, -trailingPunctuation.length) : match;

                return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailingPunctuation}`;
            });
        },
        onDateRangeChange() {
            // We can fetch signals immediately after date change
            this.fetchSignals();
        },
        async setOutOfService() {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const currentTime = new Date();
                let serviceTime = new Date();

                serviceTime.setHours(this.outOfServiceUntil.hours, this.outOfServiceUntil.minutes, 0);

                // If the selected time is in the past, move it to the next day
                if (serviceTime <= currentTime) {
                    serviceTime.setDate(serviceTime.getDate() + 1);
                }

                // Custom format the service time to "YYYY-MM-DD HH:mm:ss"
                const formattedServiceTime = this.formatDate(serviceTime);

                for (let simsAccount of this.selectedSimsAccounts) {
                    try {
                        await axios.post(`tasks/${this.task.id}/sims-accounts/${encodeURIComponent(simsAccount)}/set-out-of-service`, {
                            outOfServiceUntil: formattedServiceTime,
                        });
                        // Handle successful response
                    } catch (error) {
                        console.error('Error setting out of service:', error);
                        const message = error?.response?.data?.error?.message || this.$t('taskDetail.setOutOfServiceFailed');
                        this.$root.$refs.globalAlert.showAlert(`${simsAccount}: ${message}`, 'danger');
                    }
                }
                this.fetchSimsAccounts();
            } finally {
                this.actionInProgress = false;
            }
        },
        async deleteOutOfService() {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                for (let simsAccount of this.selectedSimsAccounts) {
                    try {
                        await axios.post(`tasks/${this.task.id}/sims-accounts/${encodeURIComponent(simsAccount)}/delete-out-of-service`);
                        // Handle successful response
                    } catch (error) {
                        console.error('Error deleting out of service:', error);
                        const message = error?.response?.data?.error?.message || this.$t('taskDetail.deleteOutOfServiceFailed');
                        this.$root.$refs.globalAlert.showAlert(`${simsAccount}: ${message}`, 'danger');
                    }
                }
                this.fetchSimsAccounts();
            } finally {
                this.actionInProgress = false;
            }
        },
        setDefaultTime() {
            let now = new Date();
            now.setHours(17, 0, 0, 0); // Sets the time to 17:00
            return now;
        },
        isCommentTarget(commentId) {
            // Extract the comment ID from the hash
            const hashCommentId = this.$route.hash.replace('#comment-', '');
            return commentId.toString() === hashCommentId;
        },
        scrollToComment(commentId) {
            const element = document.getElementById(`comment-${commentId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        },
        // The whole checklist shares one hidden input, so the subtask a picked file
        // belongs to lives only in currentSubtaskId. That is lost if the component
        // is re-created while the camera app is in front — Android discards
        // backgrounded WebViews — which used to POST subtasks/null/files. Mirror it
        // into sessionStorage, which survives the reload.
        setPendingSubtask(subtask) {
            this.currentSubtaskId = subtask.id;
            sessionStorage.setItem(PENDING_SUBTASK_UPLOAD, JSON.stringify({
                taskId: this.$route.params.taskId,
                subtaskId: subtask.id
            }));
        },
        clearPendingSubtask() {
            this.currentSubtaskId = null;
            sessionStorage.removeItem(PENDING_SUBTASK_UPLOAD);
        },
        // Runs from mounted(), before the task is fetched, so the stored id is
        // checked against the route param. A mismatch means the entry belongs to
        // another task — drop it rather than attach the photo to the wrong one.
        restorePendingSubtask() {
            const stored = sessionStorage.getItem(PENDING_SUBTASK_UPLOAD);
            if (!stored) return;
            try {
                const { taskId, subtaskId } = JSON.parse(stored);
                if (String(taskId) === String(this.$route.params.taskId) && subtaskId) {
                    this.currentSubtaskId = subtaskId;
                    return;
                }
            } catch (error) {
                console.error('Could not read pending subtask upload', error);
            }
            sessionStorage.removeItem(PENDING_SUBTASK_UPLOAD);
        },
        pickFromGallery(subtask) {
            this.setPendingSubtask(subtask);
            const el = this.$refs.subtaskFileUpload;
            // Gallery: remove capture, allow multiple if you want
            el.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
            el.removeAttribute('capture');
            el.value = null;
            el.click();
        },
        takePhoto(subtask) {
            this.setPendingSubtask(subtask);
            const el = this.$refs.subtaskFileUpload;
            // Camera: capture must be present and multiple should be off on Android
            el.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');           // keep images only
            el.setAttribute('capture', 'environment');      // or 'user'
            el.value = null;
            el.click();
        },
        async uploadSubtaskFile(event) {
            const file = event.target.files[0];
            if (!file) return;

            // No subtask to attach to — see setPendingSubtask. Fail visibly rather
            // than POSTing a literal "null" into the path for the API to reject.
            if (!this.currentSubtaskId) {
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.subtaskLostRetry'), 'danger');
                event.target.value = '';
                return;
            }

            // A 0-byte file is truthy but unusable server-side (e.g. an
            // un-downloaded cloud/placeholder file picked on mobile).
            if (file.size === 0) {
                console.error('Selected file is empty (0 bytes).');
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.emptyFile'), 'danger');
                event.target.value = '';
                return;
            }

            const maxFileSize = 10 * 1024 * 1024;
            if (file.size > maxFileSize) {
                console.error('File size exceeds the maximum limit of 5 MB.');
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.maxFilesize'), 'danger');
                // Handle the error, maybe show an alert to the user
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            // Use currentSubtaskId to determine where to upload the file. Let axios
            // set Content-Type (with the multipart boundary) from the FormData body.
            try {
                const response = await axios.post(`tasks/${this.task.id}/subtasks/${this.currentSubtaskId}/files`, formData);
                const newFile = response.data.data;
                // Find the subtask in the task.subtasks array
                const subtaskIndex = this.task.subtasks.findIndex(subtask => subtask.id === this.currentSubtaskId);
                if (subtaskIndex !== -1) {
                    // Push the new file to the subtask's files array
                    this.task.subtasks[subtaskIndex].files.push(newFile);
                }
            } catch (error) {
                console.error('Error uploading file to subtask:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.fileUploadFailed'), 'danger');
            }

            // Reset currentSubtaskId and file input for next upload
            this.clearPendingSubtask();
            event.target.value = ''; // Reset the file input
        },
        async addMe() {
            if (this.projectClosed) return;
            const storedUser = localStorage.getItem('user');
            if (!storedUser) return;

            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const userId = JSON.parse(storedUser).id;
                const response = await axios.patch(`tasks/${this.task.id}`, {
                    users: [userId] // Append userId to existing users array
                });

                // Check response status and update task.users in state if necessary
                if (response.status === 200) {
                    this.fetchTaskDetail();
                    console.log("User successfully added to the task");
                }
            } catch (error) {
                console.error("Error in adding user to task", error);
                // Optionally handle error (e.g., display error message)
            } finally {
                this.actionInProgress = false;
            }
        },
        // POST/DELETE tasks/{id}/users both answer with the task's full updated
        // assignee list, so state is patched in place rather than refetched the way
        // addMe does. isUserInvolved reads task.users, so the bottom action bar
        // re-renders on its own.
        applyTaskUsers(users) {
            this.task.users = users;
        },
        // Removing yourself is allowed, and without Priv_AppTasksAll it means losing
        // access: the next GET tasks/{id} would 403. Leave instead of refetching.
        handleSelfRemoved(users) {
            this.task.users = users;
            this.showModal = false;
            this.goBack();
        },
        // Appends the picked template to whatever is already in the field, on its
        // own line. Trailing whitespace is stripped first so a template added after
        // a half-finished line does not leave a blank line between the two.
        insertTemplate(textTemplate) {
            const current = this.task.notes2 || '';
            this.task.notes2 = current.trim()
                ? `${current.replace(/\s+$/, '')}\n${textTemplate.description}`
                : textTemplate.description;
            // A v-model write from JS fires no @input, so the autosave has to be
            // armed by hand. The .autogrow wrapper resizes itself off :data-value.
            this.scheduleNotes2Save();
            this.showModal = false;
        },
        // Inserts the picked template at the cursor (replacing any selection). A
        // textarea keeps its selection after blur, so opening the modal does not
        // lose it. Starts on a new line when text precedes the cursor and always
        // ends with one. No autosave: emailBody lives only in the composer.
        insertEmailTemplate(emailTemplate) {
            const el = this.$refs.emailBodyInput;
            const current = this.emailBody || '';
            const start = el ? el.selectionStart : current.length;
            const end = el ? el.selectionEnd : current.length;
            const before = current.slice(0, start);
            const prefix = before && !before.endsWith('\n') ? '\n' : '';
            const insert = `${prefix}${emailTemplate.description}\n`;
            this.emailBody = before + insert + current.slice(end);
            this.showModal = false;
            this.$nextTick(() => {
                if (!el) return;
                const caret = before.length + insert.length;
                el.focus();
                el.setSelectionRange(caret, caret);
            });
        },
        isNotes2Dirty() {
            return !!this.task && (this.task.notes2 ?? '') !== this.notes2SavedValue;
        },
        // Debounced autosave: fires 2s after the last keystroke. Blur, navigation
        // and every refetch flush it, so the unsaved window stays tiny.
        scheduleNotes2Save() {
            if (!this.canEditWork) return;
            clearTimeout(this.notes2SaveTimer);
            this.notes2SaveTimer = setTimeout(() => this.saveNotes2({ silent: true }), 2000);
        },
        async flushNotes2Save() {
            clearTimeout(this.notes2SaveTimer);
            if (!this.isNotes2Dirty()) return;
            await this.saveNotes2({ silent: true });
        },
        warnUnsavedNotes2(event) {
            // A pending save can't be awaited here, so warn instead. Also covers
            // the app's own window.location.reload() paths (update banner, 426).
            if (!this.isNotes2Dirty()) return;
            event.preventDefault();
            event.returnValue = '';
        },
        setNotes2Status(status, autoClear = false) {
            clearTimeout(this.notes2StatusTimer);
            this.notes2Status = status;
            if (autoClear) {
                this.notes2StatusTimer = setTimeout(() => { this.notes2Status = null; }, 3000);
            }
        },
        // silent = autosave: no toast, only the inline hint next to the Save button.
        async saveNotes2({ silent = false } = {}) {
            // The choke point for every write path: Save button, debounce, blur,
            // beforeRouteLeave, fetchTaskDetail and performAction('finish').
            if (!this.canEditWork) return;
            if (this.notes2Saving) {
                // A request is already in flight; re-arm instead of dropping this one.
                this.scheduleNotes2Save();
                return;
            }
            clearTimeout(this.notes2SaveTimer);
            // Snapshot before awaiting, so text typed during the request isn't
            // marked as saved.
            const sentValue = this.task.notes2 ?? '';
            if (silent && sentValue === this.notes2SavedValue) return;

            this.notes2Saving = true;
            this.setNotes2Status('saving');

            try {
                const response = await axios.patch(`tasks/${this.task.id}`, {
                    notes2: sentValue
                });

                if (response.status === 200) {
                    this.notes2SavedValue = sentValue;
                    this.setNotes2Status('saved', true);
                    if (!silent) {
                        this.$root.$refs.globalAlert?.showAlert(this.$t('common.saved'), 'success');
                    }
                }
            } catch (error) {
                console.error("Error saving notes2", error);
                // No toast — the inline hint carries it. notes2SavedValue is left
                // untouched, so the next edit, blur or refetch retries, and the
                // beforeunload guard still warns while the text is unsaved.
                this.setNotes2Status('error', true);
            } finally {
                this.notes2Saving = false;
            }
        },
        // act_id set = an existing act to open; null = a blank form to fill in.
        // act.id is the template id, and several acts can share one.
        actPath(act) {
            return act.act_id
                ? `/tasks/${this.task.id}/acts/${act.act_id}`
                : `/tasks/${this.task.id}/act/${act.id}`;
        },
        // The buttons behind these are already hidden to non-assignees, but the
        // modals are the only place resources are written, so re-check here too.
        openModal(type) {
            if (['materials', 'hours', 'textTemplate'].includes(type) && !this.canEditWork) return;
            // Assignee editing has its own gate: it must stay reachable in stage 0,
            // which canEditWork excludes.
            if (type === 'taskUsers' && !this.canManageTaskUsers) return;
            this.modalType = type;
            this.currentItem = null;
            this.commentToEdit = null;
            this.reminderToEdit = null;
            this.preselectResource = null;
            this.showModal = true;
        },
        // Only ever opened for 'materials' and 'hours'.
        openEditModal(type, item) {
            if (!this.canEditWork) return;
            this.modalType = type;
            this.currentItem = item;
            this.preselectResource = null;
            this.showModal = true;
        },
        openAddModalWithPreselect(type, resource) {
            if (!this.canEditWork) return;
            this.modalType = type;
            this.currentItem = null;
            this.preselectResource = resource;
            this.showModal = true;
        },
        isValidEmail(value) {
            return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
        },
        // Turns whatever is in the input into a chip. Safe to call repeatedly:
        // an empty input is a no-op, which is what lets @change and @blur share it.
        commitEmail() {
            const value = (this.emailInput || '').trim().replace(/,$/, '');
            if (!value) return;

            if (!this.isValidEmail(value)) {
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.invalidEmail'), 'danger');
                return;
            }

            if (!this.emails.includes(value)) this.emails.push(value);
            this.emailInput = '';
        },
        removeEmail(address) {
            this.emails = this.emails.filter(e => e !== address);
        },
        backspaceEmail() {
            if (!this.emailInput && this.emails.length > 0) this.emails.pop();
        },
        async sendMail() {
            this.commitEmail(); // flush anything still typed but not chipped
            if (this.emails.length === 0) {
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.invalidEmail'), 'danger');
                return; // Stop the function execution
            }

            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.post(`tasks/${this.task.id}/email`, {
                    body: this.emailBody,
                    email: this.emails,
                    signed_file_ids: this.selectedSignedFileIds,
                    technician_file_ids: this.selectedTechnicianFileIds
                });

                // Check response status and update task.users in state if necessary
                if (response.status === 200) {
                    // Append the newly sent email so the "My sent emails" list
                    // updates without a full task refetch.
                    const newEmail = response.data?.data;
                    if (newEmail) {
                        if (!this.task.sent_emails) this.task.sent_emails = [];
                        this.task.sent_emails.push(newEmail);
                    }
                    this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.emailSent'), 'success');
                    console.log("Email sent");
                    this.emailBody = null;
                    this.emails = [];
                    this.emailInput = '';
                    this.selectedSignedFileIds = [];
                    this.selectedTechnicianFileIds = [];
                }
            } catch (error) {
                console.error("Error sending email", error);
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.emailFailed'), 'danger');
                // Optionally handle error (e.g., display error message)
            } finally {
                this.actionInProgress = false;
            }
        },
        async addNewTask() {
            if (!this.canWriteTask) return;
            if (!this.newTaskTitle) {
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.titleRequired'), 'danger');
                return; // Stop the function execution
            }

            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.post(`tasks/${this.task.id}/copy`, {
                    title: this.newTaskTitle
                });

                if (response.status >= 200 && response.status < 300) {
                    // Body is { success, data: { id } }
                    const newTaskId = response.data?.data?.id;
                    this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.taskCreated'), 'success');
                    this.newTaskTitle = '';
                    if (newTaskId) {
                        // The $route watcher refetches, since this component is reused.
                        this.$router.push({ name: 'TaskDetail', params: { taskId: newTaskId } });
                        window.scrollTo(0, 0);
                    } else {
                        this.fetchTaskDetail();
                    }
                }
            } catch (error) {
                console.error("Error creating task", error);
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.taskCreateFailed'), 'danger');
                // Optionally handle error (e.g., display error message)
            } finally {
                this.actionInProgress = false;
            }
        },
        async toggleNeedsFollowup(event) {
            const value = event.target.checked;
            // The API only writes needs_followup on unfinished tasks (and still
            // answers 200), so a finished task would show "Saved" and revert.
            if (!this.canWriteTask || this.actionInProgress || this.task.finished) {
                event.target.checked = !value;
                return;
            }
            this.actionInProgress = true;

            try {
                const response = await axios.patch(`tasks/${this.task.id}`, {
                    needs_followup: value
                });
                if (response.status === 200) {
                    this.task.needs_followup = value;
                    this.fetchTaskDetail();
                    this.$root.$refs.globalAlert.showAlert(this.$t('common.saved'), 'success');
                }
            } catch (error) {
                event.target.checked = !value;
                console.error("Error patching task", error);
                this.$root.$refs.globalAlert.showAlert(this.$t('common.failed'), 'danger');
            } finally {
                this.actionInProgress = false;
            }
        },
        addItem(item, parentId = null) {
            if (parentId != null) {
                const idx = this.task.resources.findIndex(r => r.id === parentId);
                if (idx !== -1) {
                    this.task.resources.splice(idx + 1, 0, item);
                } else {
                    this.task.resources.push(item);
                }
            } else {
                this.task.resources.push(item);
            }
            this.refreshResourcesLists();
        },
        addComment() {
            this.fetchTaskDetail();
        },
        addSignature() {
            // The sign payload already carried notes2, so it is persisted — mark it
            // clean to avoid a redundant autosave PATCH on the refetch below.
            this.notes2SavedValue = this.task.notes2 ?? '';
            this.fetchTaskDetail();
        },
        openEditReminderModal(reminder) {
            this.modalType = 'reminder';
            this.reminderToEdit = reminder;
            this.showModal = true;
        },
        updateReminder(item) {
            const index = this.reminders.findIndex(r => r.id === item.id);
            if (index !== -1) this.reminders.splice(index, 1, item);
        },
        addReminder(item) {
            this.reminders.push(item);
        },
        openCommentModal(item) {
            this.modalType = 'comment';
            this.currentItem = item; // Reply: the parent comment being replied to
            this.commentToEdit = null;
            this.showModal = true; // Open the modal
        },
        toggleNotified(id) {
            this.expandedNotified = { ...this.expandedNotified, [id]: !this.expandedNotified[id] };
        },
        openEditCommentModal(comment) {
            this.modalType = 'comment';
            this.currentItem = null; // Not a reply, so no parent
            this.commentToEdit = comment; // The comment being edited
            this.showModal = true;
        },
        handleItemDeleted(deletedItemId) {
            this.task.resources = this.task.resources.filter(item => item.id !== deletedItemId);
            this.refreshResourcesLists();
        },
        updateItem(updatedItem) {
            // Find the index of the item in the main resources list
            const index = this.task.resources.findIndex(item => item.id === updatedItem.id);

            // Replace the old item with the updated item
            if (index !== -1) {
                this.task.resources.splice(index, 1, updatedItem);
            }

            // Refresh materials and hours lists
            this.refreshResourcesLists();
        },
        refreshResourcesLists() {
            this.materials = this.task.resources.filter(resource => resource.is_material_resource || resource.istitle);
            this.hours = this.task.resources.filter(resource => !resource.is_material_resource || resource.istitle);
        },
        goBack() {
            // Skip back past this task's own detail/act entries to the page the
            // user came from (e.g. the task list); fall back to home if none.
            const delta = backDeltaSkippingPath(this.$route.path);
            if (delta !== null) {
                this.$router.go(delta);
            } else {
                this.$router.push('/');
            }
        },
        async fetchTaskDetail() {
            try {
                // this.task is replaced wholesale below, so persist any pending
                // notes2 edit first — otherwise every refetch caller (comments,
                // signature, follow-up, actions) would discard what's being typed.
                await this.flushNotes2Save();
                const taskId = this.$route.params.taskId;
                const [taskResponse] = await Promise.all([
                    axios.get(`tasks/${taskId}`)
                ]);

                this.task = taskResponse.data.data;
                this.notes2SavedValue = this.task.notes2 ?? '';
                this.setNotes2Status(null);
                setDocumentTitle(this.$t('taskDetail.taskNumber', { id: this.task.id }));

                // Signed documents are selected by default for the customer e-mail
                this.selectedSignedFileIds = this.task.signed_files.map(file => file.id);

                this.materials = this.task.resources.filter(resource => resource.is_material_resource || resource.istitle);
                this.hours = this.task.resources.filter(resource => !resource.is_material_resource || resource.istitle);

                this.selectedStage = this.task.active_stage_id;
                if (this.task.project_id) {
                    this.fetchReminders();
                } else {
                    // No project, no reminders -- don't let a previous task's list linger.
                    this.reminders = [];
                }
                this.fetchSimsAccounts();
                this.loading = false;
                this.$nextTick(() => {
                    const commentId = this.$route.hash.slice(1);
                    if (commentId) {
                        const element = document.getElementById(commentId);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                            const urlWithoutHash = window.location.href.split('#')[0];
                            window.history.replaceState(null, null, urlWithoutHash);
                        }
                    }
                });
                
                this.fetchUserStatus();
            } catch (error) {
                console.error('Error fetching task detail:', error);
                this.loading = false;
            }
        },
        async fetchReminders() {
            try {
                const remindersResponse = await axios.get(`projects/${this.task.project_id}/reminders`);
                this.reminders = remindersResponse.data.data;
            } catch (error) {
                console.error('Error fetching reminders:', error);
            }
        },

        // `background` is the refresh that runs alongside the signals while the
        // card is open, so out-of-service changes made elsewhere reach this
        // page. It must not toast every 10 s, and a failed poll must not empty
        // the list (that would unmount the card).
        async fetchSimsAccounts({ background = false } = {}) {
            const taskId = this.task.id;
            try {
                const simsAccountsResponse = await axios.get(`tasks/${taskId}/sims-accounts`);
                // The route moved to another task while this was in flight.
                if (this.task?.id !== taskId) return;
                const accounts = simsAccountsResponse.data?.data;
                this.simsAccounts = Array.isArray(accounts) ? accounts : [];
                this.pruneSimsSelection();
                if (this.simsAccounts.length > 0 && !background) {
                    if (this.task.active_stage_id == 2) {
                        const anyOutOfService = this.simsAccounts.some(
                            account => account.out_of_service === true
                        );

                        if (anyOutOfService) {
                            this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.simsOutOfService'), 'warning');
                        }
                    }
                    // No signals fetch here: the card starts closed, and opening it
                    // fetches (onSimsShown -> syncSignalsPolling).
                }
            } catch (error) {
                console.error('Error fetching SIMs accounts:', error);
                if (background || this.task?.id !== taskId) return;
                // Keep simsAccounts a valid array so the template never crashes,
                // and surface the failure (e.g. USS/API timeout) to the user.
                this.simsAccounts = [];
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.simsAccountsFailed'), 'danger');
            }
        },
        /**
         * Names of the items or people blocking an action, for a toast. Capped
         * because GlobalAlert dismisses itself after 3 seconds and a long list
         * would not be readable in that time.
         */
        formatNameList(entries, max = 3) {
            const names = entries.map(s => s.name).filter(Boolean);
            return names.length > max ? names.slice(0, max).join(', ') + '…' : names.join(', ');
        },
        async updateSubtask(subtask) {
            if (!subtask || this.savingSubtaskIds.includes(subtask.id)) return;

            // Remember the optimistic value to be able to undo it on failure.
            const previousFinished = subtask.finished;
            this.savingSubtaskIds.push(subtask.id);

            try {
                // The whole subtask is sent because `notes` and `needs_attention`
                // have no save trigger of their own.
                const response = await axios.patch(`tasks/${this.task.id}/subtasks/${subtask.id}/`, subtask);
                const updatedSubtask = response.data?.data;
                const index = this.task.subtasks.findIndex(s => s.id === subtask.id);

                if (updatedSubtask && index !== -1) {
                    this.task.subtasks.splice(index, 1, updatedSubtask);
                }
                this.blockingSubtaskIds = this.blockingSubtaskIds.filter(id => id !== subtask.id);
            } catch (error) {
                console.error('Error updating subtask:', error);
                // Only the checkbox is reverted: any notes the technician typed
                // stay in place, and the textarea becomes editable again.
                subtask.finished = !previousFinished;
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saveFailed'), 'danger');
            } finally {
                this.savingSubtaskIds = this.savingSubtaskIds.filter(id => id !== subtask.id);
            }
        },
        async performAction(actionName) {
            if (this.actionInProgress) return;

            this.actionInProgress = true;
            // Set when finishing is blocked by a colleague, and acted on after the
            // refetch below so the pause question sees current task.actions.
            let pendingSelfPause = null;

            try {
                if (!this.task || !this.task.id) {
                    console.error('Task or Task ID is missing');
                    return;
                }
                if (actionName === 'finish') {
                    if (!this.task.notes2 || this.task.notes2.trim() === '') {
                        this.$root.$refs.globalAlert.showAlert(
                            this.$t('taskDetail.workDescriptionRequired'),
                            'danger'
                        );
                        return;
                    }
                    // The finish endpoint rejects this with a 409, but task.actions
                    // comes from the state machine, which does not look at
                    // checklists — so the button stays enabled and the check has
                    // to happen here, before the confirm modal.
                    if (this.blockingMandatorySubtasks.length > 0) {
                        this.blockingSubtaskIds = this.blockingMandatorySubtasks.map(s => s.id);
                        this.$root.$refs.globalAlert.showAlert(
                            this.$t('taskDetail.checklistItemsRequired', {
                                items: this.formatNameList(this.blockingMandatorySubtasks)
                            }),
                            'danger'
                        );
                        return;
                    }
                    // Always confirm finishing: the question depends on what is
                    // missing (signature / work time), but the irreversibility
                    // warning is appended in every case.
                    const missingSignature = this.task.signed_files.length === 0;
                    const missingTime = this.task.hours_visible && this.hours.length == 0;
                    let confirmKey = 'taskDetail.confirmFinish';
                    if (missingSignature && missingTime) {
                        confirmKey = 'taskDetail.confirmFinishNoSignatureNoTime';
                    } else if (missingTime) {
                        confirmKey = 'taskDetail.confirmFinishNoTime';
                    } else if (missingSignature) {
                        confirmKey = 'taskDetail.confirmFinishNoSignature';
                    }
                    // The list may date from task load if the SIMS card was never
                    // opened, and out of service can be changed elsewhere since —
                    // refetch so the warning below reflects the current state.
                    if (this.simsAccounts.length > 0) {
                        await this.fetchSimsAccounts({ background: true });
                    }
                    // Same test as fetchSimsAccounts: out_of_service is null when
                    // there is no signal, which is not the same as being in service.
                    const anySimsOutOfService = this.simsAccounts.some(
                        account => account.out_of_service === true
                    );
                    const confirmParts = [this.$t(confirmKey)];
                    if (anySimsOutOfService) {
                        confirmParts.push(this.$t('taskDetail.simsOutOfService'));
                    }
                    confirmParts.push(this.$t('taskDetail.confirmFinishIrreversible'));

                    const showFollowup = this.can('Priv_AppTasksFollowup');
                    const isConfirmed = await this.confirmAction(confirmParts.join(' '), {
                        showFollowup,
                        followupValue: !!this.task.needs_followup
                    });
                    if (!isConfirmed) return;
                    // Persist any unsaved notes2 before finishing — the finish
                    // endpoint doesn't send notes2, so unsaved edits would be lost.
                    await this.flushNotes2Save();
                    // Save needs_followup from the confirm switch while the task is
                    // still editable; a failure here aborts the finish.
                    if (showFollowup && this.confirmFollowupValue !== !!this.task.needs_followup) {
                        try {
                            await axios.patch(`tasks/${this.task.id}`, {
                                needs_followup: this.confirmFollowupValue
                            });
                        } catch (error) {
                            console.error('Error patching task', error);
                            this.$root.$refs.globalAlert.showAlert(this.$t('common.failed'), 'danger');
                            return;
                        }
                        this.task.needs_followup = this.confirmFollowupValue;
                    }
                }
                // Construct the API endpoint
                const apiEndpoint = `tasks/${this.task.id}/${actionName}`;


                // Make the API call
                await axios.post(apiEndpoint);
                await this.fetchTaskDetail(); // Fetch the task again
            } catch (error) {
                // Handle network or other errors
                console.error('Error performing action:', error);
                // The client gate above can be out of step with the server (stale
                // task data, a colleague editing the same checklist), so the 409
                // is still handled here rather than failing silently.
                if (error?.response?.status === 409) {
                    const data = error.response.data || {};
                    const blockers = data.subtasks || [];
                    // The finish endpoint answers 409 for two different reasons:
                    // unfinished mandatory checklist items, and colleagues still
                    // clocked in on the task. Only the checklist case carries
                    // `subtasks`.
                    if (blockers.length) {
                        this.blockingSubtaskIds = blockers.map(s => s.id);
                        this.$root.$refs.globalAlert.showAlert(
                            this.$t('taskDetail.checklistItemsRequired', {
                                items: this.formatNameList(blockers)
                            }),
                            'danger'
                        );
                    } else if (actionName === 'finish') {
                        // Colleagues still clocked in — the only remaining cause of
                        // a 409 here. Not a dead end: ask below whether to step off
                        // the task instead. The server may or may not name them.
                        pendingSelfPause = data.users || data.active_users || [];
                    } else {
                        // Any other action: the server may or may not name who is
                        // still working, so fall back to its own message.
                        const others = data.users || data.active_users || [];
                        let message = data.error?.message || this.$t('common.failed');
                        if (others.length) {
                            // Third argument picks the plural form. The name list
                            // is capped at 3, so it can read shorter than the
                            // count — the verb still has to agree with the count.
                            message = this.$t('taskDetail.othersWorkingCannotFinish', {
                                names: this.formatNameList(others)
                            }, others.length);
                        }
                        this.$root.$refs.globalAlert.showAlert(message, 'danger');
                    }
                }
                await this.fetchTaskDetail(); // Fetch the task again
                if (pendingSelfPause) {
                    await this.offerSelfPause(pendingSelfPause);
                }
            } finally {
                this.actionInProgress = false;
            }
        },
        // Finishing was refused because a colleague is still clocked in, which this
        // user cannot undo. Offer the one thing they can still do alone: pause the
        // task for themself. `others` is usually empty — the documented 409 body
        // carries a message but no names.
        async offerSelfPause(others) {
            // The server states which actions are available; the pause-flagged one
            // is the same button the action bar would render (`Pausile`).
            const pauseAction = (this.task?.actions || []).find(action => action.pause);
            if (!pauseAction) {
                // Nothing of this user's left to pause — keep the plain error.
                // Third argument picks the plural form. The name list is capped at
                // 3, so it can read shorter than the count — the verb still has to
                // agree with the count.
                const message = others.length
                    ? this.$t('taskDetail.othersWorkingCannotFinish', {
                        names: this.formatNameList(others)
                    }, others.length)
                    : this.$t('taskDetail.cannotFinishOthersActive');
                this.$root.$refs.globalAlert.showAlert(message, 'danger');
                return;
            }
            const message = others.length
                ? this.$t('taskDetail.othersWorkingPauseQuestion', {
                    names: this.formatNameList(others)
                }, others.length)
                : this.$t('taskDetail.someoneWorkingPauseQuestion');
            const isConfirmed = await this.confirmAction(message, {
                confirmLabel: this.$t('taskDetail.pauseForMyself')
            });
            if (!isConfirmed) return;
            try {
                await axios.post(`tasks/${this.task.id}/${pauseAction.name}`);
            } catch (error) {
                console.error('Error pausing task:', error);
                this.$root.$refs.globalAlert.showAlert(this.$t('common.failed'), 'danger');
            }
            await this.fetchTaskDetail(); // Fetch the task again
        },
        async deleteTaskFile(file, index) {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.delete(`tasks/${this.task.id}/files/${file.id}`);
                if (response.status === 200) {
                    // Find the subtask in the task's subtasks list

                    // Remove the file from the subtask's files list
                    this.task.technician_files.splice(index, 1);
                }
            } catch (error) {
                console.error('Error removing file:', error);
            } finally {
                this.actionInProgress = false;
            }
        },
        // Returns the extension (including the dot), or '' when there is none.
        fileExtension(filename) {
            const dot = (filename || '').lastIndexOf('.');
            return dot > 0 ? filename.slice(dot) : '';
        },
        // Returns the filename without its extension.
        fileBaseName(filename) {
            const dot = (filename || '').lastIndexOf('.');
            return dot > 0 ? filename.slice(0, dot) : (filename || '');
        },
        // Strips characters that are unsafe in filenames (path separators,
        // reserved and control chars) while keeping unicode letters/digits.
        sanitizeFilename(name) {
            // eslint-disable-next-line no-control-regex
            return (name || '').replace(/[<>:"/\\|?*\x00-\x1F]/g, '').replace(/\s+/g, ' ').trim();
        },
        startEditFilename(file) {
            this.editingFileId = file.id;
            this.editingFilename = this.fileBaseName(file.filename);
            this.$nextTick(() => {
                const input = Array.isArray(this.$refs.filenameInput) ? this.$refs.filenameInput[0] : this.$refs.filenameInput;
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },
        cancelEditFilename() {
            this.editingFileId = null;
            this.editingFilename = '';
        },
        async saveFilename(file) {
            const base = this.sanitizeFilename(this.editingFilename);
            if (!base) {
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.invalidFilename'), 'danger');
                return;
            }
            const newFilename = base + this.fileExtension(file.filename);
            if (newFilename === file.filename) {
                this.cancelEditFilename();
                return;
            }

            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.patch(`tasks/${this.task.id}/files/${file.id}`, {
                    filename: newFilename,
                });
                // Prefer the server's stored filename if returned, else use ours.
                file.filename = response.data?.data?.filename || newFilename;
                this.cancelEditFilename();
            } catch (error) {
                console.error('Error renaming file:', error);
                this.$root.$refs.globalAlert.showAlert(this.$t('taskDetail.renameFailed'), 'danger');
            } finally {
                this.actionInProgress = false;
            }
        },
        async deleteSignedFile(file, index) {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.delete(`tasks/${this.task.id}/files/${file.id}`);
                if (response.status === 200) {
                    // Remove the file from the task's signed files list
                    this.task.signed_files.splice(index, 1);
                }
            } catch (error) {
                console.error('Error removing file:', error);
            } finally {
                this.actionInProgress = false;
            }
        },
        async deleteSubtaskFile(subtask, file, index) {
            if (this.actionInProgress) return;
            this.actionInProgress = true;

            try {
                const response = await axios.delete(`tasks/${this.task.id}/subtasks/${subtask.id}/files/${file.id}`);
                if (response.status === 200) {
                    // Find the subtask in the task's subtasks list
                    const subtaskIndex = this.task.subtasks.findIndex(s => s.id === subtask.id);
                    if (subtaskIndex !== -1) {
                        // Remove the file from the subtask's files list
                        this.task.subtasks[subtaskIndex].files.splice(index, 1);
                    }
                }
            } catch (error) {
                console.error('Error removing file:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.fileDeleteFailed'), 'danger');
            } finally {
                this.actionInProgress = false;
            }

        },
        checkAndOpenSignatureModal() {
            if (!this.task.notes2 || this.task.notes2.trim() === '') {
                this.$root.$refs.globalAlert.showAlert(
                    this.$t('taskDetail.workDescriptionRequired'),
                    'danger'
                );
                return;
            }
            this.openModal('signature');
        },

        async handleCheckboxChange(reminder, index) {
            if (reminder.checked) {
                try {
                    const response = await axios.delete(`projects/${this.task.project_id}/reminders/${reminder.id}`);
                    if (response.status === 200) {
                        this.reminders.splice(index, 1); // Remove the reminder from the list
                    } else {
                        // If the request fails and does not return a status of 200
                        reminder.checked = false; // Revert the checkbox to its original state
                    }
                } catch (error) {
                    // Unlike the object endpoint, the project one answers 404 for a
                    // reminder that is already deleted. The outcome the user asked
                    // for already holds, so drop it rather than un-tick it.
                    if (error.response?.status === 404) {
                        this.reminders.splice(index, 1);
                        return;
                    }
                    console.error('Error removing reminder:', error);
                    reminder.checked = false; // Revert the checkbox to its original state
                    this.$root.$refs.globalAlert?.showAlert(
                        error.response?.data?.error?.message || this.$t('common.failed'),
                        'danger'
                    );
                }
            }
        },
        async requestFile(file) {
            try {
                const response = await axios({
                    url: `tasks/${this.task.id}/files/${file.id}`, // API endpoint to get the file
                    method: 'GET',
                    responseType: 'blob', // Important for files
                });

                downloadBlob(response.data, file.filename);
            } catch (error) {
                console.error('Error fetching file:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.attachmentFailed'), 'danger');
            }
        },
        async requestProjectFile(file) {
            try {
                const response = await axios({
                    url: `projects/${this.task.project_id}/files/${file.id}`, // API endpoint to get the file
                    method: 'GET',
                    responseType: 'blob', // Important for files
                });

                downloadBlob(response.data, file.filename);
            } catch (error) {
                console.error('Error fetching file:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.attachmentFailed'), 'danger');
            }
        },
        async requestSubtaskFile(subtask, file) {
            try {
                const response = await axios({
                    url: `tasks/${this.task.id}/subtasks/${subtask.id}/files/${file.id}`, // API endpoint to get the file
                    method: 'GET',
                    responseType: 'blob', // Important for files
                });

                downloadBlob(response.data, file.filename);
            } catch (error) {
                console.error('Error fetching file:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.attachmentFailed'), 'danger');
            }
        },
        openFileDialog() {
            this.$refs.fileUpload.click(); // Triggers the file input dialog
        },
        // Picking files only enqueues them. The actual POSTs run in the
        // background so every file shows up in the list right away, with its own
        // progress bar, instead of appearing one by one as each upload finishes.
        uploadFiles(event) {
            const selectedFiles = Array.from(event.target.files);
            // Cleared up front, not at the end: otherwise re-picking the same file
            // after a cancel fires no change event.
            event.target.value = null;
            if (!selectedFiles.length) return;

            const maxFileSize = 10 * 1024 * 1024;

            for (const file of selectedFiles) {
                // A 0-byte file is truthy but unusable server-side (e.g. an
                // un-downloaded cloud/placeholder file picked on mobile).
                if (file.size === 0) {
                    console.error('Selected file is empty (0 bytes):', file.name);
                    this.$root.$refs.globalAlert.showAlert(
                        this.$t('taskDetail.emptyFile'),
                        'danger'
                    );
                    continue;
                }
                if (file.size > maxFileSize) {
                    console.error('File size exceeds 10MB:', file.name);
                    this.$root.$refs.globalAlert.showAlert(
                        this.$t('taskDetail.fileTooLarge', { name: file.name }),
                        'danger'
                    );
                    continue;
                }

                this.uploads.push({
                    uid: ++this.uploadUid,
                    file,
                    name: file.name,
                    progress: 0,
                    status: 'queued',
                    controller: null,
                });
            }

            this.pumpUploads();
        },
        // Starts workers up to the cap. Safe to call again while uploads are
        // running — the technician can keep adding files to the queue.
        pumpUploads() {
            while (this.activeUploads < MAX_CONCURRENT_UPLOADS
                && this.uploads.some(u => u.status === 'queued')) {
                this.activeUploads++;
                this.runUploadWorker().finally(() => { this.activeUploads--; });
            }
        },
        async runUploadWorker() {
            for (;;) {
                const entry = this.uploads.find(u => u.status === 'queued');
                if (!entry) return;
                await this.uploadOne(entry);
            }
        },
        async uploadOne(entry) {
            // Captured up front: a refetch can swap this.task mid-upload.
            const taskId = this.task.id;

            entry.status = 'uploading';
            entry.progress = 0;
            entry.controller = new AbortController();

            const formData = new FormData();
            formData.append('file', entry.file);

            // Let axios set Content-Type (with the multipart boundary) from the FormData body.
            try {
                const response = await axios.post(`tasks/${taskId}/files`, formData, {
                    signal: entry.controller.signal,
                    onUploadProgress: (e) => {
                        entry.progress = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
                        // The bytes are out but the server is still storing the file;
                        // say so rather than leaving a bar parked at 100%.
                        if (e.total && e.loaded >= e.total) entry.status = 'finishing';
                    },
                });

                if (this.task) {
                    this.task.technician_files.push(response.data.data);
                }
            } catch (error) {
                // Cancelling is deliberate — don't nag about it.
                if (!axios.isCancel(error) && error.code !== 'ERR_CANCELED') {
                    console.error('Error uploading file:', entry.name, error);
                    this.$root.$refs.globalAlert?.showAlert(
                        this.$t('taskDetail.fileUploadFailedName', { name: entry.name }),
                        'danger'
                    );
                }
            } finally {
                this.removeUpload(entry);
            }
        },
        cancelUpload(entry) {
            if (entry.status === 'queued') {
                // Not started yet, so no request to abort — just drop it before a
                // worker can pick it up.
                this.removeUpload(entry);
                return;
            }
            entry.controller?.abort(); // uploadOne's finally removes the row
        },
        removeUpload(entry) {
            const index = this.uploads.findIndex(u => u.uid === entry.uid);
            if (index !== -1) this.uploads.splice(index, 1);
        },
        uploadStatusLabel(entry) {
            if (entry.status === 'queued') return this.$t('taskDetail.uploadQueued');
            if (entry.status === 'finishing') return this.$t('taskDetail.uploadFinishing');
            return this.$t('taskDetail.uploading');
        },

        handleUpdateEstimatedDate(data) {
            this.task.estimated_date = data.newEndDate;
            this.task.start_date = (data.timed_due_date ? data.newDate : null);
            this.task.timed_due_date = data.timed_due_date;
            this.task.agreed = data.agreed;
        },
        scrollToElement(event) {
            this.$nextTick(() => {
                const element = document.getElementById(event.target.id);
                if (element) {
                    const offsetTop = element.getBoundingClientRect().top + window.pageYOffset;
                    window.scrollTo({ top: offsetTop - 150, behavior: 'smooth' }); // 50px above the element
                }
            });
        },
        toggleSelectAll(event) {
            // If the checkbox got checked, we select all. Otherwise, we deselect all.
            if (event.target.checked) {
                // Select all items, excluding individually disabled ones.
                this.selectedSimsAccounts = this.simsAccounts.filter(acc => !acc.disabled).map(acc => acc.sims_account);
            } else {
                // Deselect all.
                this.selectedSimsAccounts = [];
            }
        },
        openSignalsPage() {
            // Read the link first, so the new tab inherits the filters as they are now.
            const href = this.signalsPageHref;
            // The new tab takes over the live updating; stop polling on this page.
            // The watcher on autoUpdate clears the interval.
            this.autoUpdate = false;
            window.open(href, '_blank', 'noopener');
        },
        async fetchSignals() {
            // The task route resolves every code authorized for the task on its own,
            // so sims_id is only sent to narrow that set down to one account.
            const params = {};
            if (this.selectedSimsAccount) {
                params.sims_id = this.selectedSimsAccount;
            }

            // Build date_from and date_to from dateRange
            if (Array.isArray(this.dateRange) && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
                // Typically dateRange[0] = start, dateRange[1] = end
                params.date_from = this.formatDate(this.dateRange[0], true);
                params.date_to = this.formatDate(this.dateRange[1], true);
            }

            try {
                this.apiSignalsError = false;
                const response = await axios.get(`tasks/${this.task.id}/sims-signals`, { params });

                this.signals = response.data.data;
            } catch (error) {
                // 404 is the API's "no signal" answer, not a broken connection.
                if (error?.response?.status !== 404) {
                    console.error('Error fetching signals:', error);
                    this.apiSignalsError = true;
                }
                this.signals = [];
            }
        },
        // Collapse events bubble, so only react to the SIMS collapse itself.
        onSimsShown(event) {
            if (event.target.id !== 'collapseSims') return;
            this.scrollToElement(event);
            this.simsOpen = true;
        },
        onSimsHidden(event) {
            if (event.target.id !== 'collapseSims') return;
            this.simsOpen = false;
        },
        // Drop selections an accounts refresh has made invalid, so the
        // out-of-service buttons never act on a removed or disabled account.
        pruneSimsSelection() {
            const selectable = new Set(this.simsAccounts.filter(acc => !acc.disabled).map(acc => acc.sims_account));
            this.selectedSimsAccounts = this.selectedSimsAccounts.filter(account => selectable.has(account));
            if (this.selectedSimsAccount && !this.simsAccounts.some(acc => acc.sims_account === this.selectedSimsAccount)) {
                this.selectedSimsAccount = '';
            }
        },
        // Signals and account statuses (out-of-service badges) refresh together.
        refreshSimsCard() {
            return Promise.all([this.fetchSignals(), this.fetchSimsAccounts({ background: true })]);
        },
        // The one gate for signal fetching: nothing while the card is closed;
        // open + auto-update polls, open without it fetches once.
        syncSignalsPolling() {
            if (!this.simsOpen) {
                this.stopAutoUpdate();
            } else if (this.autoUpdate) {
                this.startAutoUpdate();
            } else {
                this.stopAutoUpdate();
                this.refreshSimsCard();
            }
        },
        startAutoUpdate() {
            // Clear any existing interval just in case
            this.stopAutoUpdate();

            // Fetch immediately once
            this.refreshSimsCard();

            // Then set up a 10-second interval
            this.autoUpdateIntervalId = setInterval(() => {
                this.refreshSimsCard();
            }, 10000);
        },

        stopAutoUpdate() {
            if (this.autoUpdateIntervalId) {
                clearInterval(this.autoUpdateIntervalId);
                this.autoUpdateIntervalId = null;
            }
        },
        // Fetch user status from the endpoint
        async fetchUserStatus() {
            try {
                const response = await axios.get(`tasks/${this.task.id}/user_status`);
                this.userStatus = response.data.data;

                // If there's no work_hours, default to { amount: '0:00:00', ongoing: false }
                if (!this.userStatus.work_hours) {
                    this.userStatus.work_hours = { amount: 0, ongoing: false };
                }

                // If there's no transport_hours, default similarly
                if (!this.userStatus.transport_hours) {
                    this.userStatus.transport_hours = { amount: 0, ongoing: false };
                }

                // Now safely parse each amount string
                this.workSeconds = this.userStatus.work_hours.amount;
                this.transportSeconds = this.userStatus.transport_hours.amount;

                // Clear any existing interval, then start it again
                this.stopTimer();
                this.startTimer();

            } catch (error) {
                console.error('Error fetching user status:', error);
            }
        },
        formatSecondsToHMS(totalSeconds) {
            // totalSeconds could be > 24 hours, so let's just do "H:MM:SS"
            const hours = Math.floor(totalSeconds / 3600);
            const remainder = totalSeconds % 3600;
            const minutes = Math.floor(remainder / 60);
            const seconds = remainder % 60;
            // Build a string like "123:02:07"
            return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        },
        startTimer() {
            // Setup a 1-second interval
            this.timerIntervalId = setInterval(() => {
                // If ongoing, increment
                if (this.userStatus && this.userStatus.work_hours.ongoing) {
                    this.workSeconds++;
                }
                if (this.userStatus && this.userStatus.transport_hours.ongoing) {
                    this.transportSeconds++;
                }
            }, 1000);
        },
        stopTimer() {
            if (this.timerIntervalId) {
                clearInterval(this.timerIntervalId);
                this.timerIntervalId = null;
            }
        },
        getEndOfDay() {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        },
    },
    computed: {
        // Shared session cache, so opening one task after another does not refetch.
        textTemplates() {
            return getTextTemplates();
        },
        emailTemplates() {
            return getEmailTemplates();
        },
        // Guarded because the whole checklist accordion is hidden off this, and
        // a payload without subtasks would otherwise throw on .length.
        checklistSubtasks() {
            return this.task?.subtasks || [];
        },
        // Checklist items the template marks as mandatory. Despite the column
        // name, `notes_mandatory` gates completion, not notes. Title rows cannot
        // be ticked, so the server exempts them too.
        mandatorySubtasks() {
            return (this.task?.subtasks || []).filter(s => s.notes_mandatory && !s.checklist_istitle);
        },
        checklistHasMandatory() {
            return this.mandatorySubtasks.length > 0;
        },
        // The exact rule the server enforces with a 409: a mandatory item has to
        // be finished. Its notes are never required.
        blockingMandatorySubtasks() {
            return this.mandatorySubtasks.filter(s => !s.finished);
        },
        // All contact arrays are read through guards rather than straight off
        // task: the payload has reshaped them more than once, so an API that
        // predates a field returns nothing and the block would throw on .length.
        taskContacts() {
            return this.task?.contacts || [];
        },
        // The customer's own contact person, from customers.contact_*.
        customerContacts() {
            return this.task?.customer_contacts || [];
        },
        // The project card's contact rows — what customer_contacts used to hold.
        projectCustomerContacts() {
            return this.task?.project_customer_contacts || [];
        },
        maintenanceProjectContacts() {
            return this.task?.maintenance_project_contacts || [];
        },
        // Forus staff roles, not customer-side people.
        companyContacts() {
            return this.task?.project_contacts || [];
        },
        hasAnyContacts() {
            return this.taskContacts.length > 0 || this.customerContacts.length > 0
                || this.projectCustomerContacts.length > 0 || this.maintenanceProjectContacts.length > 0;
        },
        // Everyone on the customer side, across all four cards. Contacts not yet
        // added, so the datalist stops offering duplicates.
        availableEmailContacts() {
            const all = [
                ...this.taskContacts,
                ...this.customerContacts,
                ...this.projectCustomerContacts,
                ...this.maintenanceProjectContacts,
            ].filter(c => c.email && !this.emails.includes(c.email));
            // The same person can sit on both the customer and the project card.
            return [...new Map(all.map(c => [c.email, c])).values()];
        },
        googleMapsUrl() {
            return googleMapsAddressUrl(this.task.project_address);
        },
        wazeUrl() {
            return wazeAddressUrl(this.task.project_address);
        },
        googleMapsUrlCoordinates() {
            return googleMapsCoordinatesUrl(this.task.project_coordinates);
        },
        wazeUrlCoordinates() {
            return wazeCoordinatesUrl(this.task.project_coordinates);
        },
        mapTarget() {
            return mapLinkTarget();
        },
        /**
         * Up to three action buttons still fit on one row. A fourth cannot, and
         * because Bootstrap sets flex-shrink: 0 on row children the odd one out
         * wraps onto a row of its own at full width. Pair them up instead, but
         * only on narrow screens — from md up all four fit as they always did.
         */
        actionColumnClass() {
            return this.task?.actions?.length > 3 ? 'col-6 col-md' : 'col';
        },
        isUserInvolved() {
            // Check if user data is available in local storage
            const storedUser = localStorage.getItem('user');
            if (!storedUser) return false;

            const userId = JSON.parse(storedUser).id;

            // Check if 'users' array is defined in the task and contains the current user ID
            return this.task && ((Array.isArray(this.task.users) && this.task.users.some(user => user.id === userId)));
        },
        // The API refuses every write on a task whose project card is closed with
        // a 403, and returns no actions for it. Guarded because the field is newer
        // than the rest of the payload; an API that predates it means "open".
        projectClosed() {
            return !!this.task?.project_not_active;
        },
        // Assigned to the task AND allowed to write to it. isUserInvolved on its
        // own only answers the first half, so it still drives the "add me" half of
        // the action bar, while every edit affordance hangs off this.
        canWriteTask() {
            return this.isUserInvolved && !this.projectClosed;
        },
        // Checklist rows are only writable while the task is actually in work.
        // Stage alone used to decide it; the closed-project gate rides along here
        // so the ticks, notes and photo buttons all go quiet together.
        canEditChecklist() {
            return this.canWriteTask && !this.task?.finished && this.task?.active_stage_id === 1;
        },
        // Work-in-progress data — notes2, materials, hours — used to be guarded by
        // stage alone, so anyone who could open the task could write to it. Being
        // assigned is required now, same as the rest of the view. The task test
        // matters: task is null until the first fetch, and fetchTaskDetail flushes
        // notes2 before it exists.
        canEditWork() {
            return !!this.task
                && !this.task.finished
                && !this.task.confirmed
                && this.task.active_stage_id >= 1
                && this.canWriteTask;
        },
        currentUserId() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user).id : null;
        },
        // Every assignee change, the caller's own included, needs
        // Priv_AppTasksEditUsers. The server also refuses everything once the task
        // is finished, but a dead button is worse than no button.
        canManageTaskUsers() {
            return !!this.task && !this.task.finished && !this.task.confirmed && this.canWriteTask
                && this.can('Priv_AppTasksEditUsers');
        },
        // The same privilege covers adding and removing anyone, not just yourself.
        canManageOtherTaskUsers() {
            return this.canManageTaskUsers;
        },
        // Unassigning yourself mid-timer would strand a running work or transport
        // record, so the modal hides the control while one is going.
        selfTimerRunning() {
            return !!this.userStatus
                && (!!this.userStatus.work_hours?.ongoing || !!this.userStatus.transport_hours?.ongoing);
        },
        isCommentExpanded() {
            const commentHash = this.$route.hash;
            // Check if the hash starts with "#comment-" which indicates a comment ID is present
            return commentHash.startsWith("#comment-");
        },
        /**
         * Standalone signals page, carrying the filters currently set on this table.
         * On "--All--" we leave sims_id out so that page resolves every account itself.
         */
        signalsPagePath() {
            const query = {};

            if (this.selectedSimsAccount) {
                query.sims_id = this.selectedSimsAccount;
            }
            if (Array.isArray(this.dateRange) && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
                query.date_from = this.formatDate(this.dateRange[0], true);
                query.date_to = this.formatDate(this.dateRange[1], true);
            }
            if (this.autoUpdate) {
                query.auto = '1';
            }

            return { name: 'SimsSignals', params: { taskId: this.task.id }, query };
        },
        signalsPageHref() {
            return this.$router.resolve(this.signalsPagePath).href;
        },
        allSelected() {
            // Selection stays available on finished and confirmed tasks: accounts
            // left out of service have to be putable back in service afterwards.
            // Only setting out of service is still restricted to an in-work task.
            if (!this.canWriteTask) {
                return false;
            }
            // Only consider selectable (non-disabled) accounts.
            const selectableAccounts = this.simsAccounts.filter(acc => !acc.disabled);
            return selectableAccounts.length > 0 && this.selectedSimsAccounts.length === selectableAccounts.length;
        },
        /**
         * Convert workSeconds back into H:i:s
         */
        formattedWorkTime() {
        return this.formatSecondsToHMS(this.workSeconds);
        },
        /**
         * Convert transportSeconds back into H:i:s
         */
        formattedTransportTime() {
            return this.formatSecondsToHMS(this.transportSeconds);
        },
    }

};
</script>

<style scoped>

/* 20 visual lines, so a long project memo cannot push the rest of the task off
   the screen. white-space must stay set on the same element as the -webkit-box
   display (.text-multiline supplies pre-line), or the \n characters linkifyText
   leaves in the text stop rendering as line breaks. */
.memo4-clamped {
    display: -webkit-box;
    -webkit-line-clamp: 20;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Auto-growing work description. The wrapper's ::after holds a hidden copy of the
   text and sizes the grid cell; the textarea is stretched over the same cell. No
   JS measurement, so it is correct on first paint, after a refetch, and even when
   the accordion is collapsed while the text changes.
   The shared rule must mirror .form-control's box exactly (custom.css:2109) or the
   copy wraps differently from the real text. */
.autogrow {
    display: grid;
}

.autogrow::after {
    content: attr(data-value) " ";
    visibility: hidden;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    /* Same width as the textarea's border, but invisible. */
    border: var(--bs-border-width) solid transparent;
}

.autogrow > textarea {
    overflow: hidden;
    resize: none;
}

.autogrow > textarea,
.autogrow::after {
    grid-area: 1 / 1 / 2 / 2;
    box-sizing: border-box;
    min-height: 100px;
    padding: 0.75rem 1.25rem;
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.36;
}

.item-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    
    border-radius: 5px;
    z-index: 1031;
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    max-height: 100%; /* Adjust as needed */
    width: 100%;
    padding: 20px;
    overflow-y: auto; /* Makes the content scrollable */
}
</style>